import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios, { addTokenToUrl, readProfileUser } from '../utils/api';
import './GeneratorV2.css';
import PlatformIcon from '../components/PlatformIcon';
import PublicPreviewLink from '../components/PublicPreviewLink';
import { extractPreviewGenerationId, navigatePreviewWindow, openPendingPreviewWindow, requestPreviewAccess } from '../utils/previewAccess';
import LocaleSelect from '../components/LocaleSelect';
import ComponentExample from '../components/ComponentExample';
import ThemeExample from '../components/ThemeExample';
import ProgressIcon from '../components/ProgressIcon';
import { formatBalanceUSD } from '../utils/adminHelpers';
import { generatorActiveKey, generatorDraftKey, generatorRequestKey, readGeneratorDraft } from '../utils/generatorDraft';
import {
  GeneratorV2FormState,
  GeneratorV2State,
  GeneratorStep,
  HtmlTemplate,
  ComponentSelection,
  CssTheme,
  ContentTheme,
  RecommendedComponent,
  GenerationRequestV2,
  ThemeMode,
  TemplateWarning,
  TemplateAvailability,
  TemplateAccessInfo,
  SiteType,
  GENERATOR_STEPS
} from '../types/generatorV2';

type ProgressComponent = {
  id: string | number;
  name: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  order: number;
  variantCode?: string | null;
  containerName?: string | null;
  containerId?: number | null;
  componentId?: number | null;
};

type BatchGenerationStatus = 'queued' | 'processing' | 'completed' | 'failed';

type BatchGenerationRun = {
  index: number;
  templateId?: number;
  templateName?: string;
  status: BatchGenerationStatus;
  generationId: string | null;
  queuePosition: number | null;
  previewUrl: string | null;
  downloadUrl: string | null;
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  elapsedMs: number | null;
  stepName: string;
  percentage: number;
  currentComponent: ProgressCurrentComponent | null;
  progress: GenerationProgressPayload | null;
  progressState: 'ok' | 'progress missing';
};

export type TemplateComponents = { required: string[]; optional: string[]; total: number };

type TemplateMetadata = Record<string, unknown> & {
  theme?: string;
  recommendedTheme?: string;
  allowedThemes?: unknown;
  themeDescription?: string;
  isSmokeTemplate?: boolean;
  isTestTemplate?: boolean;
  columns_count?: unknown;
  columnsCount?: unknown;
  columns?: unknown;
  layout?: Record<string, unknown>;
  is_multipage?: unknown;
  siteType?: unknown;
};

type BackgroundPreset = {
  id: number;
  name: string;
  subtype?: string;
  color_scheme?: string;
  performance_weight?: number;
  preview_url?: string;
  preview_html?: string;
};

type CuratedBackground = {
  id: number;
  name: string;
  type?: string;
  color_scheme?: string;
  url?: string;
};

const backgroundPreviewImage = (url?: string | null): string | undefined => {
  if (!url) return undefined;
  return url.startsWith('css:') ? url.slice(4) : `url("${url}")`;
};

const backgroundPreviewDocument = (html: string, scheme: 'light' | 'dark') => `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>*{margin:0;padding:0;box-sizing:border-box}html,body{width:100%;height:100%;background:${scheme === 'dark' ? '#111827' : '#f8fafc'}}.global-background-dynamic *:not(.prism-ray),.global-background-dynamic *:not(.prism-ray)::before,.global-background-dynamic *:not(.prism-ray)::after{animation-delay:-8s}</style></head><body>${html}</body></html>`;

type ProgressCurrentComponent = {
  name: string;
  index: number;
  total: number;
  container?: string;
  variantCode?: string | null;
};

type GenerationProgressPayload = {
  status?: string;
  currentStep?: number;
  totalSteps?: number;
  currentComponent?: ProgressCurrentComponent | null;
  components?: ProgressComponent[];
  componentsCount?: number;
  stepName?: string;
  percentage?: number;
  queuePosition?: number;
  error?: string | null;
  generationTimeMs?: number;
  duration?: number;
  warnings?: TemplateWarning[];
  availability?: TemplateAvailability;
  dbGenerationId?: number;
  downloadUrl?: string;
  previewUrl?: string;
};

type ProgressPollingTimerApi = {
  startPolling: (tick: () => void | Promise<void>, delayMs: number) => void;
  stopPolling: () => void;
  isActiveRef: React.MutableRefObject<boolean>;
};

// Custom Hook: таймер поллинга на рекурсивном setTimeout (без setInterval)
export function useProgressPollingTimer(): ProgressPollingTimerApi {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isActiveRef = useRef(false);
  const revisionRef = useRef(0);

  const stopPolling = useCallback(() => {
    isActiveRef.current = false;
    revisionRef.current++;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const startPolling = useCallback((tick: () => void | Promise<void>, delayMs: number) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    const revision = ++revisionRef.current;
    isActiveRef.current = true;

    const scheduleNext = () => {
      if (!isActiveRef.current || revision !== revisionRef.current) return;
      timeoutRef.current = setTimeout(() => {
        try {
          const result = tick();
          if (result && typeof result.then === 'function') void result.catch(() => {}).finally(scheduleNext);
          else scheduleNext();
        } catch { scheduleNext(); }
      }, delayMs);
    };

    scheduleNext();
  }, []);
  useEffect(() => stopPolling, [stopPolling]);

  return { startPolling, stopPolling, isActiveRef };
}

// Custom Hook: глобальный tooltip (fixed, не режется overflow карточек)
function useGlobalTooltip(ready: boolean) {
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const tooltipActiveRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const tooltipEl = tooltipRef.current;
    if (!tooltipEl) return;

    const hide = () => {
      tooltipActiveRef.current?.removeAttribute('aria-describedby');
      tooltipEl.classList.remove('is-open');
      tooltipEl.setAttribute('aria-hidden', 'true');
      tooltipActiveRef.current = null;
    };

    const position = (x: number, y: number) => {
      const pad = 12;
      const offset = 12;

      // После обновления текста нужно измерить реальные размеры
      const rect = tooltipEl.getBoundingClientRect();
      let left = x + offset;
      let top = y + offset;

      const maxLeft = window.innerWidth - rect.width - pad;
      const maxTop = window.innerHeight - rect.height - pad;

      if (left > maxLeft) left = Math.max(pad, maxLeft);
      if (top > maxTop) top = Math.max(pad, maxTop);

      tooltipEl.style.left = `${Math.round(left)}px`;
      tooltipEl.style.top = `${Math.round(top)}px`;
    };

    const show = (target: HTMLElement, text: string, x: number, y: number) => {
      tooltipEl.textContent = text;
      tooltipEl.classList.add('is-open');
      tooltipEl.setAttribute('aria-hidden', 'false');
      tooltipActiveRef.current = target;
      target.setAttribute('aria-describedby', 'generator-tooltip');
      position(x, y);
    };

    const onPointerOver = (e: PointerEvent) => {
      const target = (e.target as HTMLElement | null)?.closest?.('[data-tip]') as HTMLElement | null;
      if (!target) return;
      const text = target.getAttribute('data-tip');
      if (!text) return;
      show(target, text, e.clientX, e.clientY);
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!tooltipActiveRef.current) return;
      position(e.clientX, e.clientY);
    };

    const onPointerOut = (e: PointerEvent) => {
      if (!tooltipActiveRef.current) return;
      const related = e.relatedTarget as Node | null;
      if (related && tooltipActiveRef.current.contains(related)) return;
      hide();
    };

    const onFocusIn = (e: FocusEvent) => {
      const target = (e.target as HTMLElement | null)?.closest?.('[data-tip]') as HTMLElement | null;
      if (!target) return;
      const text = target.getAttribute('data-tip');
      if (!text) return;
      const r = target.getBoundingClientRect();
      show(target, text, r.left + r.width / 2, r.top + r.height / 2);
    };

    const onFocusOut = () => {
      hide();
    };

    const onResizeOrScroll = () => {
      if (tooltipActiveRef.current) hide();
    };

    const onClick = (event: MouseEvent) => {
      const target = (event.target as HTMLElement | null)?.closest?.('.u2-info[data-tip]') as HTMLElement | null;
      if (!target) { hide(); return; }
      const rect = target.getBoundingClientRect();
      show(target, target.dataset.tip || '', rect.left, rect.bottom);
    };
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') hide(); };

    document.addEventListener('pointerover', onPointerOver, true);
    document.addEventListener('pointermove', onPointerMove, true);
    document.addEventListener('pointerout', onPointerOut, true);
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onResizeOrScroll);
    window.addEventListener('scroll', onResizeOrScroll, true);

    return () => {
      document.removeEventListener('pointerover', onPointerOver, true);
      document.removeEventListener('pointermove', onPointerMove, true);
      document.removeEventListener('pointerout', onPointerOut, true);
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('focusout', onFocusOut, true);
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onResizeOrScroll);
      window.removeEventListener('scroll', onResizeOrScroll, true);
    };
  }, [ready]);

  return { tooltipRef };
}

const GeneratorV2: React.FC<{ adminMode?: boolean }> = ({ adminMode = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const requestedMode = new URLSearchParams(location.search).get('mode');
  const initialRequestedMode = useRef(requestedMode);
  const [interfaceMode, setInterfaceMode] = useState<'quick' | 'full'>(requestedMode === 'quick' ? 'quick' : 'full');
  const quickMode = interfaceMode === 'quick';
  const visibleSteps: GeneratorStep[] = quickMode ? [1, 3, 5, 6, 7] : [1, 2, 3, 4, 5, 6, 7];
  const [currentStep, setCurrentStep] = useState<GeneratorStep>(1);
  const nextStep = visibleSteps[visibleSteps.indexOf(currentStep) + 1];
  const [furthestStep, setFurthestStep] = useState<GeneratorStep>(1);
  const workspaceRef = useRef<HTMLElement | null>(null);
  const componentRequestRef = useRef(0);
  const previewRequestRef = useRef(0);
  const [componentLoad, setComponentLoad] = useState({ templateId: null as number | null, loading: false, error: '' });
  const [checkedForm, setCheckedForm] = useState('');
  const [pricing, setPricing] = useState<{ single_page: number; multi_page: number; free?: { remaining: number; next: string | null } | null } | null>(null);
  const [account, setAccount] = useState<{ balance: number; generation_limit: number } | null>(null);
  const [draftUserId, setDraftUserId] = useState<number | null>(null);
  const [draftRestored, setDraftRestored] = useState(false);
  const [backgroundUploading, setBackgroundUploading] = useState(false);
  const [backgroundUploadError, setBackgroundUploadError] = useState('');
  const [uploadedPreviewUrl, setUploadedPreviewUrl] = useState('');
  const [uploadedBackgroundName, setUploadedBackgroundName] = useState('Загруженный фон');
  const activeGenerationRef = useRef<string | null>(null);
  const progressRequestRef = useRef<(id: string) => Promise<void>>(async () => {});
  const generationSubmitRef = useRef(false);
  
  // Состояние формы
  const [formData, setFormData] = useState<GeneratorV2FormState>({
    templateType: 'single',
    siteType: 'landing', // #Gen-Multi: По умолчанию лендинг
    htmlTemplate: null,
    theme: 'corporate-blue',
    themeMode: 'template',
    themeDescription: '',
    siteContentTheme: '',
    siteDescriptionOverride: '',
    useRecommendedComponents: true,
    selectedComponents: [],
    optionalComponentsState: {}, // 'random' | 'include' | 'exclude'
    componentVariantChoice: {}, // 'random' | variantCode
    platform: 'facebook',
    geo: 'international',
    contentLanguage: 'ru',
    legalDocsLanguage: '', // '' = язык сайта, 'en' = английский
    companyName: '',
    companyEmail: '',
    companyAddress: '',
    companyDomain: '',
    advancedMode: false,
    communicationStyle: 'professional',
    specialRequirements: '',
    debugMode: false,
    // 🆕 #F-Background: Настройки глобального фона
    backgroundEnabled: true,
    backgroundMode: 'static',
    backgroundPerformance: 'balanced',
    backgroundPresetId: null,
    backgroundCuratedId: null,
    backgroundUploadId: '',
    // 🆕 Прозрачность компонентов (null = использовать настройку шаблона или глобальную)
    componentOpacity: null,
    // #VisualModernization: единый эффект секционных фонов (none по умолчанию)
    componentVisualEffect: 'none'
  });

  const initialFormRef = useRef(formData);
  const formKey = JSON.stringify({ formData, interfaceMode });
  const currentFormRef = useRef(formData);
  currentFormRef.current = formData;
  const currentFormKeyRef = useRef(formKey);
  currentFormKeyRef.current = formKey;

  // Состояние приложения
  const [state, setState] = useState<GeneratorV2State>({
    loading: true,
    error: '',
    htmlTemplates: [],
    availableComponents: [],
    componentsByType: {},
    themes: [],
    contentThemes: [],
    selectedTemplate: null,
    recommendedComponents: [],
    preview: null,
    previewLoading: false,
    previewTotalComponents: 0, // 🆕 Количество уникальных типов компонентов
    previewTotalInstances: 0, // 🆕 Общее количество экземпляров
    previewWarnings: [],
    templateAccess: null,
    generationInProgress: false,
    generationResult: null
  });

  const busy = state.generationInProgress;
  const componentsReady = componentLoad.templateId === formData.htmlTemplate && !componentLoad.loading && !componentLoad.error && state.recommendedComponents.length > 0;
  const previewIsCurrent = checkedForm === formKey && Boolean(state.preview);
  const generationPrice = pricing ? (formData.siteType === 'multipage' ? pricing.multi_page : pricing.single_page) : null;
  // Бесплатные генерации сервиса (после регистрации, в день, в месяц) тратятся раньше лимита и баланса.
  const freeRemaining = Number(pricing?.free?.remaining) > 0 ? Number(pricing?.free?.remaining) : 0;
  const canAffordGeneration = account !== null && generationPrice !== null &&
    (generationPrice === 0 || freeRemaining > 0 || account.generation_limit > 0 || account.balance >= generationPrice);
  const contentError = !formData.siteContentTheme ? 'Выберите тематику сайта.'
    : !quickMode && formData.companyEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.companyEmail.trim()) ? 'Проверьте email компании, например hello@example.com.' : '';
  const nextDisabled = busy || (currentStep === 1 && (!formData.htmlTemplate || !componentsReady)) || (currentStep === 3 && Boolean(contentError)) || (currentStep === 6 && backgroundUploading);

  useEffect(() => {
    setFurthestStep(prev => Math.max(prev, currentStep) as GeneratorStep);
    const heading = workspaceRef.current?.querySelector<HTMLElement>('h2');
    heading?.setAttribute('tabindex', '-1');
    heading?.focus({ preventScroll: true });
    workspaceRef.current?.scrollIntoView?.({ block: 'start' });
  }, [currentStep]);

  // Изменившаяся конфигурация требует новой проверки, старый ответ не применяется.
  useEffect(() => {
    previewRequestRef.current += 1;
    setCheckedForm('');
    setState(prev => ({ ...prev, preview: null, previewWarnings: [], templateAccess: null, previewLoading: false, generationResult: null }));
    return () => { previewRequestRef.current += 1; };
  }, [formKey]);

  // 🆕 #F-115: Состояние прогресса генерации
  const [generationProgress, setGenerationProgress] = useState({
    status: 'idle', // idle, queued, processing, completed, failed
    currentStep: 0,
    totalSteps: 7,
    currentComponent: null as { name: string; index: number; total: number; container?: string; variantCode?: string | null } | null,
    components: [] as ProgressComponent[],
    componentsCount: 0,
    stepName: '',
    percentage: 0,
    error: null as string | null,
    queuePosition: null as number | null
  });

  // Фильтр темы в режиме «Из библиотеки»: 'light' | 'dark'
  const [themePalette, setThemePalette] = useState<'light' | 'dark'>('light');

  // 🆕 Batch генерация (admin-only)
  const [batchCount, setBatchCount] = useState(1);
  const [batchMode, setBatchMode] = useState<'sequential' | 'parallel'>('sequential');
  const [batchRuns, setBatchRuns] = useState<BatchGenerationRun[]>([]);
  // #BatchTestTemplates: выбор тестовых шаблонов для пакетного прогона.
  // 0 элементов — обычный режим (batchCount × formData.htmlTemplate).
  // 1 элемент — обычный режим с этим шаблоном, редактирование компонентов доступно.
  // 2+ элементов — batch-test режим: компоненты заблокированы, запуск последовательный.
  const [selectedTestTemplateIds, setSelectedTestTemplateIds] = useState<number[]>([]);
  const [batchInProgress, setBatchInProgress] = useState(false);
  const [batchPreviewBlockedUrls, setBatchPreviewBlockedUrls] = useState<string[]>([]);
  const [batchDownloadBlockedUrls, setBatchDownloadBlockedUrls] = useState<string[]>([]);
  const [batchExpandedRuns, setBatchExpandedRuns] = useState<number[]>([]);
  const generationResultRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!state.generationResult) return;
    const frame = window.requestAnimationFrame(() => {
      generationResultRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
      generationResultRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [state.generationResult]);

  const batchTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const batchSessionRef = useRef(0);
  const batchCancelRef = useRef(false);
  const batchTickerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // 🆕 #F-115: Таймер polling (setTimeout-цепочка вместо setInterval)
  const {
    startPolling: startProgressPolling,
    stopPolling: stopProgressPolling
  } = useProgressPollingTimer();

  const clearBatchTimers = useCallback(() => {
    batchCancelRef.current = true;
    batchTimersRef.current.forEach((timer) => {
      clearTimeout(timer);
    });
    batchTimersRef.current.clear();
    if (batchTickerRef.current) {
      clearTimeout(batchTickerRef.current);
      batchTickerRef.current = null;
    }
  }, []);

  // 🆕 Тикер для обновления времени batch-итераций (setTimeout-цепочка)
  const hasActiveBatchRuns = batchRuns.some(run => run.status === 'queued' || run.status === 'processing');

  useEffect(() => {
    if (!hasActiveBatchRuns) {
      return;
    }

    const tick = () => {
      setBatchRuns(prev => {
        const now = Date.now();
        let changed = false;
        const next = prev.map(run => {
          if (!run.startedAt) {
            return run;
          }
          if (run.status !== 'queued' && run.status !== 'processing') {
            return run;
          }
          const elapsedMs = Math.max(0, now - new Date(run.startedAt).getTime());
          if (run.elapsedMs === elapsedMs) {
            return run;
          }
          changed = true;
          return { ...run, elapsedMs };
        });
        return changed ? next : prev;
      });
      batchTickerRef.current = setTimeout(tick, 1000);
    };

    batchTickerRef.current = setTimeout(tick, 1000);

    return () => {
      if (batchTickerRef.current) {
        clearTimeout(batchTickerRef.current);
        batchTickerRef.current = null;
      }
    };
  }, [hasActiveBatchRuns]);
  
  // 🆕 Таймер генерации и советы
  const [generationStartTime, setGenerationStartTime] = useState<number | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  
  // Советы и факты для показа во время генерации
  const generationTips = useMemo(() => [
    'Проценты отражают готовность этапов, а не оставшееся время.',
    'Тексты и изображения обычно занимают большую часть времени.',
    'Счётчик блоков обновляется после получения результата.',
    'Многостраничный сайт может собираться дольше лендинга.',
    'Готовый сайт появится в разделе «Мои сайты».'
  ], []);
  
  // Обновляем таймер каждую секунду во время генерации
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (state.generationInProgress && generationStartTime) {
      interval = setInterval(() => {
        setElapsedSeconds(Math.floor((Date.now() - generationStartTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [state.generationInProgress, generationStartTime]);
  
  // Меняем совет каждые 5 секунд
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (state.generationInProgress) {
      interval = setInterval(() => {
        setCurrentTipIndex(prev => (prev + 1) % generationTips.length);
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [state.generationInProgress, generationTips.length]);

  useEffect(() => {
    return () => {
      clearBatchTimers();
    };
  }, [clearBatchTimers]);
  
  const [showSmokeTemplates, setShowSmokeTemplates] = useState(false);
  const [showTestTemplates, setShowTestTemplates] = useState(false);
  const [userRole, setUserRole] = useState<string>('user');

  // UI (v5): фильтры для шага выбора шаблона
  const [templateSearch, setTemplateSearch] = useState<string>('');
  const [backgroundSearch, setBackgroundSearch] = useState<string>('');
  const [backgroundLoadVersion, setBackgroundLoadVersion] = useState(0);
  const [backgroundLoadError, setBackgroundLoadError] = useState('');
  const selectedBackgroundRef = useRef<HTMLDivElement | null>(null);
  const [templateColumnsFilter, setTemplateColumnsFilter] = useState<'all' | '1' | '2' | '3'>('all');

  // UI (v5): модалка для превью (шаблон/фон)
  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    title: string;
    kind: 'image' | 'dynamic' | 'template';
    template?: HtmlTemplate;
    imageUrl: string | null;
    presetId: number | null;
  }>({
    isOpen: false,
    title: '',
    kind: 'image',
    imageUrl: null,
    presetId: null
  });
  const previewTriggerRef = useRef<HTMLElement | null>(null);
  const previewCloseButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!previewModal.isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    previewCloseButtonRef.current?.focus();
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        event.preventDefault();
        previewCloseButtonRef.current?.focus();
        return;
      }
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setPreviewModal((prev) => ({ ...prev, isOpen: false }));
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = previousOverflow;
      previewTriggerRef.current?.focus();
    };
  }, [previewModal.isOpen]);

  // UI (v5): глобальный tooltip (fixed, не режется overflow карточек)
  const { tooltipRef } = useGlobalTooltip(!state.loading);
  // 🆕 фоновые списки
  const [bgPresets, setBgPresets] = useState<BackgroundPreset[]>([]);
  const [bgCurated, setBgCurated] = useState<CuratedBackground[]>([]);
  // 🆕 превью динамического фона (iframe srcDoc)
  const [dynamicPreview, setDynamicPreview] = useState<{
    html: string;
    loading: boolean;
    error: string;
    presetId: number | null;
  }>({
    html: '',
    loading: false,
    error: '',
    presetId: null
  });
  const dynamicPreviewRequestIdRef = useRef(0);
  const dynamicPreviewAbortRef = useRef<AbortController | null>(null);
  
  // 🆕 #Core-Lang: Состояние для списка языков
  const [availableLanguages, setAvailableLanguages] = useState<Array<{
    code: string;
    name: string;
    nativeName: string;
    direction: 'ltr' | 'rtl';
    popular: boolean;
  }>>([]);
  
  // 🆕 #Core-Legal: Состояние для юрисдикций и платформ
  const [legalConfig, setLegalConfig] = useState<{
    jurisdictions: Array<{ code: string; name: string; nameRu: string; requiredDocs?: string[]; }>;
    platforms: Array<{ code: string; name: string; nameRu: string; additionalDocs?: string[]; }>;
  }>({
    jurisdictions: [],
    platforms: []
  });
  
  const isAdmin = adminMode && userRole === 'admin';
  const debugModeActive = isAdmin && formData.debugMode;
  const orderedComponents = useMemo(
    () =>
      [...generationProgress.components].sort(
        (a, b) => (a.order || 0) - (b.order || 0)
      ),
    [generationProgress.components]
  );

  useEffect(() => {
    let disposed = false;
    const fetchUserRole = async () => {
      try {
        const response = await axios.get('/api/auth/profile');
        const profile = readProfileUser(response.data);
        if (disposed) return;
        setUserRole(profile?.role || 'user');
        if (Number.isFinite(Number(profile?.balance)) && Number.isFinite(Number(profile?.generation_limit))) {
          setAccount({ balance: Number(profile?.balance), generation_limit: Number(profile?.generation_limit) });
        }
        const userId = Number(profile?.id);
        if (Number.isSafeInteger(userId) && userId > 0) {
          const draft = readGeneratorDraft(userId, initialFormRef.current, adminMode);
          if (draft) {
            setFormData(draft.form);
            const requested = initialRequestedMode.current;
            const mode = requested === 'quick' || requested === 'full' ? requested : draft.mode;
            setInterfaceMode(mode);
            setCurrentStep(mode === 'quick' && (draft.step === 2 || draft.step === 4) ? 3 : draft.step);
            setFurthestStep(draft.step);
            setDraftRestored(true);
          }
          setDraftUserId(userId);
        }
      } catch {
        if (!disposed) setUserRole('user');
      }
    };

    fetchUserRole();
    const loadPricing = async () => {
      try {
        const response = await axios.get('/api/templates/pricing');
        if (!disposed && Number.isFinite(response.data?.single_page) && Number.isFinite(response.data?.multi_page)) setPricing(response.data);
      } catch { /* Стоимость остаётся неизвестной, нулевая цена не подставляется. */ }
    };
    void loadPricing();
    return () => { disposed = true; };
  }, [adminMode]);

  useEffect(() => {
    if (!draftUserId) return;
    try {
      if (formData.htmlTemplate) sessionStorage.setItem(generatorDraftKey(draftUserId, adminMode), JSON.stringify({ version: 1, form: formData, step: currentStep, mode: interfaceMode }));
      else sessionStorage.removeItem(generatorDraftKey(draftUserId, adminMode));
    } catch { /* Недоступность storage не мешает генерации. */ }
  }, [draftUserId, formData, currentStep, interfaceMode, adminMode]);

  useEffect(() => {
    if (!draftUserId) return;
    try {
      const saved = JSON.parse(sessionStorage.getItem(generatorActiveKey(draftUserId, adminMode)) || 'null');
      if (!saved || typeof saved.generationId !== 'string' || !/^[a-zA-Z0-9_-]{1,128}$/.test(saved.generationId) || !Number.isFinite(saved.startedAt) || saved.startedAt > Date.now() + 60000 || Date.now() - saved.startedAt > 86400000) return;
      activeGenerationRef.current = saved.generationId;
      setCurrentStep(7); setFurthestStep(7); setGenerationStartTime(saved.startedAt);
      setState(prev => ({ ...prev, generationInProgress: true }));
      void progressRequestRef.current(saved.generationId).finally(() => {
        if (activeGenerationRef.current === saved.generationId) startProgressPolling(() => progressRequestRef.current(saved.generationId), 2500);
      });
    } catch { /* Повреждённый storage не запускает новую генерацию. */ }
  }, [draftUserId, startProgressPolling, adminMode]);

  useEffect(() => {
    if (!state.generationResult?.id) return;
    let disposed = false;
    // Списание прошло — обновляем остаток бесплатных генераций.
    void axios.get('/api/templates/pricing').then(response => {
      if (!disposed && Number.isFinite(response.data?.single_page) && Number.isFinite(response.data?.multi_page)) setPricing(response.data);
    }).catch(() => { /* Подсказка о бесплатных обновится при следующем открытии. */ });
    void axios.get('/api/auth/profile').then(response => {
      const profile = readProfileUser(response.data);
      if (!disposed && Number.isFinite(Number(profile?.balance)) && Number.isFinite(Number(profile?.generation_limit))) {
        setAccount({ balance: Number(profile?.balance), generation_limit: Number(profile?.generation_limit) });
      }
    }).catch(() => { /* Итог генерации остаётся доступным при временном сбое профиля. */ });
    return () => { disposed = true; };
  }, [state.generationResult?.id]);

  // 🆕 #Core-Lang: Загрузка списка языков при инициализации
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await axios.get('/api/languages');
        if (response.data?.success && response.data?.data?.languages) {
          setAvailableLanguages(response.data.data.languages);
        }
      } catch (error) {
        console.error('Ошибка загрузки списка языков:', error);
        // Fallback на базовые языки если API недоступен
        setAvailableLanguages([
          { code: 'en', name: 'English', nativeName: 'English', direction: 'ltr', popular: true },
          { code: 'ru', name: 'Russian', nativeName: 'Русский', direction: 'ltr', popular: true },
          { code: 'es', name: 'Spanish', nativeName: 'Español', direction: 'ltr', popular: true },
          { code: 'fr', name: 'French', nativeName: 'Français', direction: 'ltr', popular: true },
          { code: 'de', name: 'German', nativeName: 'Deutsch', direction: 'ltr', popular: true }
        ]);
      }
    };

    fetchLanguages();
  }, []);

  // 🆕 #Core-Legal: Загрузка конфига юрисдикций и платформ
  useEffect(() => {
    const fetchLegalConfig = async () => {
      try {
        const response = await axios.get('/api/generator/legal-config');
        if (response.data) {
          setLegalConfig(response.data);
        }
      } catch (error) {
        console.error('Ошибка загрузки конфига юрисдикций:', error);
        // Fallback
        setLegalConfig({
          jurisdictions: [
            { code: 'international', name: 'International / Worldwide', nameRu: 'Международный / Весь мир' },
            { code: 'eu', name: 'European Union (GDPR)', nameRu: 'Европейский союз (GDPR)' },
            { code: 'us', name: 'United States', nameRu: 'США' },
            { code: 'ru', name: 'Russia (152-FZ)', nameRu: 'Россия (152-ФЗ)' }
          ],
          platforms: [
            { code: 'facebook', name: 'Facebook / Meta Ads', nameRu: 'Facebook / Meta Ads' },
            { code: 'google', name: 'Google Ads', nameRu: 'Google Ads' },
            { code: 'universal', name: 'Universal', nameRu: 'Универсальный' }
          ]
        });
      }
    };

    fetchLegalConfig();
  }, []);

  const templateCollections = useMemo(() => {
    const filteredByType = state.htmlTemplates.filter(template => {
      if (!isAdmin && template.is_published === false) return false;
      const templateIsMultipage = isTemplateMultipage(template);
      return formData.siteType === 'multipage' ? templateIsMultipage : !templateIsMultipage;
    });

    const smokeTemplates = filteredByType.filter(template => template.metadata?.isSmokeTemplate);
    const testTemplates = filteredByType.filter(template => template.metadata?.isTestTemplate);
    const regularTemplates = filteredByType.filter(
      template => !template.metadata?.isSmokeTemplate && !template.metadata?.isTestTemplate
    );

    return {
      smokeTemplates,
      testTemplates,
      regularTemplates
    };
  }, [state.htmlTemplates, formData.siteType, isAdmin]);

  useEffect(() => {
    if (adminMode || !formData.htmlTemplate) return;
    const selected = state.htmlTemplates.find(template => template.id === formData.htmlTemplate);
    // Старый админский черновик не переносит тестовый шаблон в обычный мастер.
    if (selected && (selected.is_published === false || selected.metadata?.isTestTemplate || selected.metadata?.isSmokeTemplate)) {
      setFormData(prev => ({ ...prev, htmlTemplate: null, debugMode: false, selectedComponents: [] }));
      setCurrentStep(1);
      setFurthestStep(1);
    }
  }, [adminMode, formData.htmlTemplate, state.htmlTemplates]);

  // #BatchTestTemplates: все текущие тестовые шаблоны по siteType + производные от выбора.
  const availableTestTemplateIds = useMemo(
    () => templateCollections.testTemplates.map(tpl => tpl.id),
    [templateCollections.testTemplates]
  );
  // Чистим выбор при смене siteType: удаляем id, которых больше нет в списке.
  useEffect(() => {
    setSelectedTestTemplateIds(prev => {
      const filtered = prev.filter(id => availableTestTemplateIds.includes(id));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [availableTestTemplateIds]);

  const isBatchTestMode = selectedTestTemplateIds.length >= 2;
  const runAllTestTemplates = isBatchTestMode;
  const isAllTestTemplatesSelected = availableTestTemplateIds.length > 0
    && selectedTestTemplateIds.length === availableTestTemplateIds.length;

  const toggleAllTestTemplates = useCallback((checked: boolean) => {
    setSelectedTestTemplateIds(checked ? [...availableTestTemplateIds] : []);
  }, [availableTestTemplateIds]);

  const toggleTestTemplateSelected = useCallback((templateId: number) => {
    setSelectedTestTemplateIds(prev => (
      prev.includes(templateId)
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    ));
  }, []);

  // В batch-test режиме держим formData.htmlTemplate синхронизированным с первым
  // выбранным тестовым шаблоном — иначе preview/компоненты шага 4 ссылаются на старый id.
  useEffect(() => {
    if (!isBatchTestMode) {
      return;
    }
    const firstSelected = selectedTestTemplateIds[0];
    if (firstSelected && formData.htmlTemplate !== firstSelected) {
      setFormData(prev => ({ ...prev, htmlTemplate: firstSelected }));
    }
  }, [isBatchTestMode, selectedTestTemplateIds, formData.htmlTemplate]);

  // Если выбран ровно 1 тестовый шаблон — делаем его активным для wizard-flow.
  useEffect(() => {
    if (selectedTestTemplateIds.length !== 1) {
      return;
    }
    const onlyId = selectedTestTemplateIds[0];
    if (formData.htmlTemplate !== onlyId) {
      setFormData(prev => ({ ...prev, htmlTemplate: onlyId }));
    }
  }, [selectedTestTemplateIds, formData.htmlTemplate]);

  const selectedTemplate = useMemo(
    () => state.htmlTemplates.find(template => template.id === formData.htmlTemplate) || null,
    [state.htmlTemplates, formData.htmlTemplate]
  );
  const currentTemplateMetadata = normalizeTemplateMetadata(selectedTemplate?.metadata);
  const currentTemplateTheme =
    readString(currentTemplateMetadata.theme) || readString(currentTemplateMetadata.recommendedTheme);
  const currentTemplateAllowedThemes = readStringArray(currentTemplateMetadata.allowedThemes);
  const selectedTemplateComponents = useMemo(() => getTemplateComponents(selectedTemplate), [selectedTemplate]);
  const selectedTemplateColumnsCount = useMemo(
    () => (selectedTemplate ? getTemplateColumnsCount(selectedTemplate) : null),
    [selectedTemplate]
  );
  const selectedContentTheme = useMemo(
    () => state.contentThemes.find(theme => theme.key === formData.siteContentTheme) || null,
    [state.contentThemes, formData.siteContentTheme]
  );

  const finalComponentsForReview = useMemo(() => {
    const out: Array<{ key: string; label: string; tip: string; isRequired: boolean }> = [];

    (state.recommendedComponents || []).forEach((comp, idx) => {
      const componentName = comp.componentName || comp.variantCode?.split('-')[0] || comp.variantCode || `component-${idx}`;
      const variantChoice = (formData.useRecommendedComponents ? resolveRuleVariantChoice(comp) : formData.componentVariantChoice[componentName]) || 'random';
      const optState = formData.useRecommendedComponents ? 'random' : formData.optionalComponentsState[componentName] || 'random';
      const isExcluded = !comp.isRequired && optState === 'exclude';
      if (isExcluded) return;

      const displayVariant = variantChoice === 'random' ? `${getComponentLabel(componentName)} · автовыбор варианта` : `${getComponentLabel(componentName)} · ${variantChoice}`;
      const prefix = comp.isRequired ? 'всегда' : optState === 'include' ? 'включён' : 'может быть добавлен';

      out.push({
        key: `${comp.componentId}-${componentName}-${idx}`,
        label: `${prefix} · ${displayVariant}`,
        tip: getComponentTip(componentName),
        isRequired: !!comp.isRequired
      });
    });

    return out;
  }, [formData.useRecommendedComponents, formData.componentVariantChoice, formData.optionalComponentsState, state.recommendedComponents]);

  const legalDocsForReview = useMemo(() => {
    const geo = formData.geo || 'international';
    const platform = formData.platform || 'universal';
    const jurisdiction = legalConfig.jurisdictions.find((j) => j.code === geo);
    const platformConfig = legalConfig.platforms.find((p) => p.code === platform);

    const requiredDocs = jurisdiction?.requiredDocs || ['privacy-policy', 'terms-of-service'];
    const additionalDocs = platformConfig?.additionalDocs || [];
    const allDocs = Array.from(new Set([...requiredDocs, ...additionalDocs]));

    return allDocs.map((id) => ({
      id,
      title: LEGAL_DOC_NAMES[id] || id
    }));
  }, [formData.geo, formData.platform, legalConfig.jurisdictions, legalConfig.platforms]);

  const shouldShowProgressPanel =
    state.generationInProgress || (debugModeActive && generationProgress.components.length > 0);
  const displayedError = state.error || (['failed', 'cancelled'].includes(generationProgress.status) ? generationProgress.error : '');
  const previewTheme = quickMode || formData.themeMode === 'template' ? currentTemplateTheme || formData.theme : formData.theme;
  const previewColorScheme: 'light' | 'dark' = state.themes.find(item => item.cssClass === previewTheme)?.isDark || previewTheme.startsWith('dark-') ? 'dark' : 'light';

  const getSeverityLabel = (severity?: TemplateWarning['severity']) => {
    switch (severity) {
      case 'error':
        return 'Критично';
      case 'warning':
        return 'Важно';
      default:
        return 'Информация';
    }
  };

  const renderWarningsPanel = (warnings?: TemplateWarning[] | null, title = 'Предупреждения') => {
    if (!warnings || warnings.length === 0) {
      return null;
    }

    return (
      <div className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
        <div className="u2-cardInner">
          <div className="u2-row" style={{ marginBottom: 10 }}>
            <div>
              <div className="u2-strong">{title}</div>
              <div className="u2-muted" style={{ fontSize: 12 }}>
                Всего предупреждений: {warnings.length}
              </div>
            </div>
            <span className="u2-badge">{warnings.length}</span>
          </div>

          <div className="u2-grid" style={{ gap: 10 }}>
            {warnings.map((warning, index) => {
              const severity = warning.severity || 'info';
              const pillClass = severity === 'error' ? 'is-bad' : severity === 'warning' ? 'is-warn' : '';
              const label = getSeverityLabel(severity);

              return (
                <div
                  key={`${warning.type}-${warning.containerId || ''}-${index}`}
                  className="u2-card"
                  style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}
                >
                  <div className="u2-cardInner">
                    <div className="u2-row" style={{ alignItems: 'flex-start' }}>
                      <div style={{ minWidth: 0 }}>
                        <div className="u2-strong" style={{ fontSize: 13 }}>
                          {warning.message}
                        </div>
                        <div className="u2-muted" style={{ fontSize: 12, marginTop: 6 }}>
                          {warning.containerName ? `Контейнер: ${warning.containerName}` : 'Контейнер: —'}
                        </div>
                      </div>
                      <span className={`u2-pill ${pillClass}`}>{label}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderTemplateAccessSummary = (access?: TemplateAccessInfo | null) => {
    if (!access) {
      return null;
    }

    const {
      templatePublished = true,
      hasUnpublishedContainers = false,
      hasRequiredUnpublishedComponents = false
    } = access;

    return (
      <div className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
        <div className="u2-cardInner">
          <div className="u2-row" style={{ marginBottom: 10 }}>
            <div>
              <div className="u2-strong">Доступность шаблона</div>
              <div className="u2-muted" style={{ fontSize: 12 }}>
                Статусы публикации и доступность для пользователей.
              </div>
            </div>
            <span className={`u2-pill ${templatePublished ? 'is-ok' : 'is-warn'}`}>
              {templatePublished ? 'опубликован' : 'admin'}
            </span>
          </div>

          <div className="u2-toolbarGroup" style={{ justifyContent: 'flex-start' }}>
            <span className={`u2-pill ${templatePublished ? 'is-ok' : 'is-warn'}`}>
              {templatePublished ? 'Опубликован' : 'Только администратор'}
            </span>
            {hasUnpublishedContainers ? <span className="u2-pill is-warn">Неопубликованные контейнеры</span> : null}
            {hasRequiredUnpublishedComponents ? <span className="u2-pill is-bad">Нет обязательных компонентов</span> : null}
          </div>

          <div className="u2-muted" style={{ fontSize: 12, marginTop: 10 }}>
            {hasUnpublishedContainers || hasRequiredUnpublishedComponents
              ? 'Проверьте статусы блоков перед публикацией.'
              : 'Все блоки опубликованы и готовы к использованию.'}
          </div>
        </div>
      </div>
    );
  };

  const renderAvailabilitySummary = (availability?: TemplateAvailability | null) => {
    if (!availability) {
      return null;
    }

    const {
      hasUnpublishedContainers,
      hasMenuReminders,
      hasUnpublishedComponents,
      totalContainers,
      totalComponents
    } = availability;

    return (
      <div className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
        <div className="u2-cardInner">
          <div className="u2-row" style={{ marginBottom: 10 }}>
            <div>
              <div className="u2-strong">Структура контейнеров</div>
              <div className="u2-muted" style={{ fontSize: 12 }}>
                {(totalContainers ?? 0).toLocaleString('ru-RU')} контейнеров · {(totalComponents ?? 0).toLocaleString('ru-RU')} компонентов
              </div>
            </div>
            <span className="u2-badge">структура</span>
          </div>

          <div className="u2-toolbarGroup" style={{ justifyContent: 'flex-start' }}>
            <span className={`u2-pill ${hasUnpublishedContainers ? 'is-warn' : 'is-ok'}`}>
              {hasUnpublishedContainers ? 'Есть неопубликованные контейнеры' : 'Все контейнеры опубликованы'}
            </span>
            <span className={`u2-pill ${hasUnpublishedComponents ? 'is-warn' : 'is-ok'}`}>
              {hasUnpublishedComponents ? 'Компоненты требуют публикации' : 'Компоненты опубликованы'}
            </span>
            {hasMenuReminders ? <span className="u2-pill is-warn">Есть контейнеры без меню</span> : null}
          </div>
        </div>
      </div>
    );
  };

  const getThemeName = (slug?: string | null) => {
    if (!slug) {
      return '—';
    }
    const themeEntry = state.themes.find(theme => theme.cssClass === slug);
    return (themeEntry?.name || slug).replace(/^[^A-Za-zА-Яа-я0-9]+/, '');
  };

  const resolvedThemeSlug =
    state.preview?.theme ||
    selectedTemplate?.metadata?.theme ||
    (formData.themeMode === 'list' ? formData.theme : 'corporate-blue');

  const loadTemplateComponents = useCallback(async (templateId: number) => {
    const requestId = ++componentRequestRef.current;
    setComponentLoad({ templateId, loading: true, error: '' });
    setState(prev => ({ ...prev, recommendedComponents: [] }));
    try {
      const response = await axios.get(`/api/generation/html-templates/${templateId}/components`);
      if (requestId !== componentRequestRef.current || currentFormRef.current.htmlTemplate !== templateId) return;
      const { containers, recommendedComponents, totalContainers } = response.data.data;
      if (!Array.isArray(recommendedComponents) || !recommendedComponents.length) throw new Error('В шаблоне нет доступных блоков. Выберите другой шаблон.');
      setComponentLoad({ templateId, loading: false, error: '' });

      // Обновляем выбранный шаблон через prev-state, чтобы не тянуть зависимости в useEffect.
      setState((prev) => {
        const selectedTemplatePrev = prev.htmlTemplates.find((t) => t.id === templateId);
        const updatedTemplate = selectedTemplatePrev
          ? { ...selectedTemplatePrev, container_count: totalContainers || containers?.length || 0 }
          : null;
        return { ...prev, selectedTemplate: updatedTemplate, recommendedComponents };
      });

      // 🆕 Инициализируем componentVariantChoice значениями по умолчанию
      const initialVariantChoice: Record<string, 'random' | string> = {};
      const initialOptionalState: Record<string, 'random' | 'include' | 'exclude'> = {};

      recommendedComponents.forEach((comp: RecommendedComponent) => {
        const componentName = comp.componentName || String(comp.variantCode || '').split('-')[0] || comp.variantCode;
        const specificVariant = resolveRuleVariantChoice(comp);
        // По умолчанию — случайный выбор, но для specific фиксируем вариант
        initialVariantChoice[componentName] = specificVariant || 'random';
        // Для необязательных — случайно (50%)
        if (!comp.isRequired) {
          initialOptionalState[componentName] = 'random';
        }
      });

      const recommendedSelections: ComponentSelection[] = recommendedComponents.map((comp: RecommendedComponent) => ({
        componentId: comp.componentId,
        variantCode: comp.variantCode,
        containerId: comp.containerId,
        containerName: comp.containerName,
        instances: comp.minInstances
      }));

      setFormData((prev) => {
        // После восстановления оставляем только допустимые варианты текущих правил.
        recommendedComponents.forEach((comp: RecommendedComponent) => {
          const name = comp.componentName || String(comp.variantCode || '').split('-')[0] || comp.variantCode;
          const choice = prev.componentVariantChoice[name];
          if (!resolveRuleVariantChoice(comp) && (choice === 'random' || comp.availableVariants?.some(v => v.variantCode === choice))) initialVariantChoice[name] = choice;
          if (!comp.isRequired && prev.optionalComponentsState[name]) initialOptionalState[name] = prev.optionalComponentsState[name];
        });
        const base = {
          ...prev,
          componentVariantChoice: initialVariantChoice,
          optionalComponentsState: initialOptionalState
        };
        return prev.useRecommendedComponents ? { ...base, selectedComponents: recommendedSelections } : base;
      });
    } catch (error: unknown) {
      if (requestId !== componentRequestRef.current || currentFormRef.current.htmlTemplate !== templateId) return;
      setComponentLoad({ templateId, loading: false, error: 'Не удалось загрузить блоки шаблона. Повторите загрузку или выберите другой шаблон.' });
    }
  }, []);

  // Загрузка начальных данных
  useEffect(() => {
    loadInitialData();
  }, []);

  // Загрузка пресетов/картинок для фона
  useEffect(() => {
    let disposed = false;
    setBackgroundLoadError('');
    const loadBackgroundData = async () => {
      try {
        const response = await axios.get(`/api/generation/backgrounds/catalog?colorScheme=${previewColorScheme}`);
        if (disposed) return;
        if (!response.data?.success) throw new Error('Каталог недоступен');
        if (response.data?.success) {
          setBgPresets(response.data.presets || []);
          setBgCurated(response.data.backgrounds || []);
        }
      } catch (err) {
        if (!disposed) setBackgroundLoadError('Не удалось загрузить каталог фонов. Выбранные настройки сохранены.');
      }
    };
    loadBackgroundData();
    return () => { disposed = true; };
  }, [backgroundLoadVersion, previewColorScheme]);

  useEffect(() => {
    const uploadId = formData.backgroundUploadId;
    setUploadedPreviewUrl('');
    if (!uploadId) return;
    const controller = new AbortController();
    let objectUrl = '';
    void axios.get(`/api/generation/backgrounds/uploads/${encodeURIComponent(uploadId)}`, { responseType: 'blob', signal: controller.signal }).then(response => {
      if (controller.signal.aborted) return;
      objectUrl = URL.createObjectURL(response.data);
      setUploadedPreviewUrl(objectUrl);
    }).catch(() => { if (!controller.signal.aborted) setBackgroundUploadError('Не удалось открыть загруженный фон. Загрузите его заново.'); });
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [formData.backgroundUploadId]);

  const uploadOwnBackground = async (file?: File) => {
    if (!file || backgroundUploading) return;
    setBackgroundUploadError('');
    if (!['image/png', 'image/jpeg'].includes(file.type)) { setBackgroundUploadError('Выберите PNG или JPEG.'); return; }
    if (file.size > 8 * 1024 * 1024) { setBackgroundUploadError('Максимальный размер файла — 8 МБ.'); return; }
    setBackgroundUploading(true);
    try {
      const payload = new FormData(); payload.append('image', file);
      const response = await axios.post('/api/generation/backgrounds/upload', payload);
      if (!response.data?.success) throw new Error('Фон не загружен');
      setUploadedBackgroundName(response.data.data.name);
      setFormData(previous => ({ ...previous, backgroundEnabled: true, backgroundMode: 'static', backgroundUploadId: response.data.data.uploadId, backgroundCuratedId: null, backgroundPresetId: null }));
    } catch (error) { setBackgroundUploadError(getApiErrorMessage(error, 'Не удалось загрузить фон. Повторите загрузку.')); }
    finally { setBackgroundUploading(false); }
  };

  // Превью динамического фона через iframe srcDoc
  useEffect(() => {
    const isDynamic = formData.backgroundEnabled && formData.backgroundMode === 'dynamic';
    const presetId = formData.backgroundPresetId;
    const selectedPreset = isDynamic && presetId
      ? bgPresets.find((preset) => preset.id === presetId) || null
      : null;

    if (!isDynamic || !presetId || !selectedPreset) {
      if (dynamicPreviewAbortRef.current) {
        dynamicPreviewAbortRef.current.abort();
        dynamicPreviewAbortRef.current = null;
      }
      setDynamicPreview({
        html: '',
        loading: false,
        error: '',
        presetId: null
      });
      return;
    }

    const requestId = dynamicPreviewRequestIdRef.current + 1;
    dynamicPreviewRequestIdRef.current = requestId;

    if (dynamicPreviewAbortRef.current) {
      dynamicPreviewAbortRef.current.abort();
    }
    const controller = new AbortController();
    dynamicPreviewAbortRef.current = controller;

    setDynamicPreview({
      html: '',
      loading: true,
      error: '',
      presetId
    });

    const payload = {
      mode: 'dynamic',
      presetId,
      overlayOpacity: 0.15,
      colorScheme: previewColorScheme
    };

    axios
      .post('/api/generation/backgrounds/preview', payload, { signal: controller.signal })
      .then((res) => {
        if (dynamicPreviewRequestIdRef.current !== requestId) return;
        if (res.data?.success) {
          setDynamicPreview({
            html: res.data?.html || '',
            loading: false,
            error: '',
            presetId
          });
        } else {
          setDynamicPreview({
            html: '',
            loading: false,
            error: res.data?.error || 'Не удалось загрузить превью динамического фона',
            presetId
          });
        }
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (dynamicPreviewRequestIdRef.current !== requestId) return;
        setDynamicPreview({
          html: '',
          loading: false,
          error: getApiErrorMessage(err, 'Не удалось загрузить превью динамического фона'),
          presetId
        });
      });

    return () => {
      controller.abort();
    };
  }, [formData.backgroundEnabled, formData.backgroundMode, formData.backgroundPresetId, bgPresets, previewColorScheme]);

  const dynamicPreviewSrcDoc = useMemo(() => dynamicPreview.html ? backgroundPreviewDocument(dynamicPreview.html, previewColorScheme) : '', [dynamicPreview.html, previewColorScheme]);

  // Загрузка компонентов шаблона при выборе
  useEffect(() => {
    if (formData.htmlTemplate) {
      loadTemplateComponents(formData.htmlTemplate);
    }
    return () => { componentRequestRef.current += 1; };
  }, [formData.htmlTemplate, loadTemplateComponents]);

  useEffect(() => {
    if (!formData.htmlTemplate) {
      return;
    }

    const selected = state.htmlTemplates.find(template => template.id === formData.htmlTemplate);
    if (selected?.metadata?.isSmokeTemplate) {
      setShowSmokeTemplates(true);
    }
    if (selected?.metadata?.isTestTemplate) {
      setShowTestTemplates(true);
    }
  }, [formData.htmlTemplate, state.htmlTemplates]);

  // Автозагрузка preview при переходе на шаг 7 (финальная проверка перед генерацией)
  useEffect(() => {
    if (currentStep === 7 && componentsReady && !contentError && !busy && !batchInProgress && formData.htmlTemplate) {
      generatePreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, formKey, componentsReady]);

  const loadInitialData = async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: '' }));

      // Загружаем все шаблоны сразу (landing + multipage), чтобы не было пустых состояний при переключении
      const [templatesResponse, themesResponse, contentThemesResponse] = await Promise.all([
        axios.get('/api/generation/html-templates?siteType=all'),
        axios.get('/api/generation/themes'),
        axios.get('/api/generation/content-themes')
      ]);

      const themesList: CssTheme[] = themesResponse.data.data || [];
      const contentThemes: ContentTheme[] = contentThemesResponse.data.data || [];

      setState(prev => ({
        ...prev,
        htmlTemplates: templatesResponse.data.data,
        themes: themesList,
        contentThemes,
        loading: false
      }));

      if (themesList.length > 0) {
        setFormData(prev => ({
          ...prev,
          theme: themesList.some(theme => theme.cssClass === prev.theme)
            ? prev.theme
            : themesList[0].cssClass
        }));
      }

      if (contentThemes.length > 0) {
        setFormData(prev => ({
          ...prev,
          siteContentTheme: contentThemes.some(theme => theme.key === prev.siteContentTheme)
            ? prev.siteContentTheme
            : contentThemes[0].key
        }));
      }
    } catch (error: unknown) {
      setState(prev => ({
        ...prev,
        error: getApiErrorMessage(error, 'Ошибка загрузки данных'),
        loading: false
      }));
    }
  };

  const openImagePreviewModal = (title: string, imageUrl: string) => {
    previewTriggerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setPreviewModal({
      isOpen: true,
      title,
      kind: 'image',
      imageUrl,
      presetId: null
    });
  };

  const openDynamicPreviewModal = (title: string, presetId: number) => {
    previewTriggerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setPreviewModal({
      isOpen: true,
      title,
      kind: 'dynamic',
      imageUrl: null,
      presetId
    });
  };

  const closePreviewModal = () => {
    setPreviewModal({
      isOpen: false,
      title: '',
      kind: 'image',
      imageUrl: null,
      presetId: null
    });
  };

  const renderTemplateCard = (template: HtmlTemplate) => {
    const isSelected = formData.htmlTemplate === template.id;
    const cols = getTemplateColumnsCount(template);
    const comps = getTemplateComponents(template);
    const compTotal = comps.total || template.container_count || 0;
    const colComp = countColumnComponents(template);

    const name = typeof template.name === 'string' && template.name.trim() ? template.name.trim() : 'Шаблон';
    const desc =
      typeof template.description === 'string' && template.description.trim()
        ? template.description.trim()
        : 'Шаблон без описания';

    return (
      <article
        key={template.id}
        className={`u2-previewCard ${isSelected ? 'is-selected' : ''}`}
        aria-label={name}
        onClick={() => handleFormChange('htmlTemplate', template.id)}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
        }}
      >
        <TemplateLayoutPreview template={template} />
        <div className="u2-previewHover">
          <button
            type="button"
            className="u2-btn is-secondary"
            onClick={(e) => {
              e.stopPropagation();
              previewTriggerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
              setPreviewModal({ isOpen: true, kind: 'template', template, title: `Схема: ${name}`, imageUrl: null, presetId: null });
            }}
          >
            Схема шаблона
          </button>
          <button
            type="button"
            className={`u2-btn ${isSelected ? 'is-success' : 'is-primary'}`}
            onClick={(e) => {
              e.stopPropagation();
              if (isSelected) handleStepNext();
              else handleFormChange('htmlTemplate', template.id);
            }}
            disabled={isSelected && nextDisabled}
            aria-label={isSelected ? `Далее с шаблоном ${name}` : `Выбрать шаблон ${name}`}
          >
            {isSelected ? 'Далее' : 'Выбрать'}
          </button>
        </div>
        <div className="u2-previewMeta">
          <div className="u2-previewTitleRow">
            <div style={{ minWidth: 0 }}>
              <div className="u2-previewTitle">{name}</div>
              <div className="u2-previewDesc">{desc}</div>
            </div>
            {isAdmin && <span className="u2-badge">ID {template.id}</span>}
          </div>
          <div className="u2-chipRow">
            <span className="u2-pill">Колонки: {cols}</span>
            <span className="u2-pill wp-componentCount">{compTotal} типов блоков</span>
            {isAdmin && <span className="u2-pill">Блоки колонок: {colComp}</span>}
          </div>
        </div>
      </article>
    );
  };

  const handleFormChange = (field: keyof GeneratorV2FormState, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'htmlTemplate' && prev.htmlTemplate !== value ? { componentVariantChoice: {}, optionalComponentsState: {}, selectedComponents: [] } : {}),
      ...((field === 'backgroundCuratedId' || field === 'backgroundPresetId') && value !== null ? { backgroundUploadId: '' } : {})
    }));
  };

  // #Gen-Multi: Переключение типа сайта без повторного запроса
  const handleSiteTypeChange = (newSiteType: SiteType) => {
    if (newSiteType === formData.siteType) return;
    setFurthestStep(1);
    setFormData(prev => ({
      ...prev,
      siteType: newSiteType,
      templateType: newSiteType === 'multipage' ? 'multi-page' : 'single',
      htmlTemplate: null,
      selectedComponents: [], componentVariantChoice: {}, optionalComponentsState: {}
    }));

    setState(prev => ({
      ...prev,
      selectedTemplate: null,
      recommendedComponents: [],
      error: ''
    }));

    // Сбрасываем UI фильтры списка шаблонов (v5)
    setTemplateSearch('');
    setTemplateColumnsFilter('all');
  };

  const handleThemeModeChange = (mode: ThemeMode) => {
    setFormData(prev => {
      let nextTheme = prev.theme;
      if (mode === 'list' && !nextTheme && state.themes.length > 0) {
        nextTheme = state.themes[0].cssClass;
      }
      return {
        ...prev,
        themeMode: mode,
        theme: nextTheme,
        themeDescription: prev.themeDescription
      };
    });
    // Синхронизируем фильтр палитры с текущей выбранной темой
    if (mode === 'list') {
      const currentThemeEntry = state.themes.find(t => t.cssClass === formData.theme);
      setThemePalette(currentThemeEntry?.isDark ? 'dark' : 'light');
    }
  };

  const handleStepNext = () => {
    if (nextDisabled) return;
    if (currentStep < 7) {
      // Валидация текущего шага
      if (currentStep === 1 && !formData.htmlTemplate) {
        setState(prev => ({ ...prev, error: 'Выберите HTML шаблон' }));
        return;
      }

      if (nextStep) setCurrentStep(nextStep);
      setState(prev => ({ ...prev, error: '' }));
    }
  };

  const handleStepBack = () => {
    if (busy || batchInProgress) return;
    if (currentStep > 1) {
      setCurrentStep(visibleSteps[visibleSteps.indexOf(currentStep) - 1] || 1);
      setState(prev => ({ ...prev, error: '' }));
    }
  };

  // Вспомогательная функция для формирования списка компонентов
  const getComponentsToSend = () => {
    // Если режим автовыбора — не передаём компоненты, бэкенд сам сформирует
    if (formData.useRecommendedComponents) {
      return undefined;
    }
    
    // Если режим настройки включен, передаём ВСЕ компоненты с их настройками
    if (state.recommendedComponents.length > 0) {
      return state.recommendedComponents.map(comp => {
        const componentName = comp.componentName || comp.variantCode.split('-')[0] || comp.variantCode;
        
        // Проверяем состояние необязательного компонента
        const optState = formData.optionalComponentsState[componentName] || 'random';
        
        // Проверяем, выбран ли конкретный вариант или случайный
        const variantChoice = formData.componentVariantChoice[componentName];
        const isRandomVariant = !variantChoice || variantChoice === 'random';
        
        // Формируем объект компонента
        const componentData: ComponentSelection = {
          componentId: comp.componentId,
          variantCode: isRandomVariant ? null : variantChoice,
          containerId: comp.containerId || comp.containerIds?.[0],
          containerName: comp.containerName || comp.containerNames?.[0],
          instances: comp.minInstances,
          componentName: componentName,
          randomVariant: isRandomVariant
        };
        
        // Добавляем информацию о включении/выключении для необязательных
        if (!comp.isRequired) {
          if (optState === 'exclude') {
            componentData.forceInclude = false;
            componentData.forceExclude = true; // 🆕 Явно помечаем как выключенный
          } else if (optState === 'include') {
            componentData.forceInclude = true;
          } else {
            // 'random' — помечаем для бэкенда
            componentData.randomInclude = true;
          }
        }
        
        // Если выбран конкретный вариант, обновляем componentId
        if (!isRandomVariant) {
          const selectedVariant = comp.availableVariants?.find(v => v.variantCode === variantChoice);
          if (selectedVariant) {
            componentData.componentId = selectedVariant.id;
          }
        }
        
        return componentData;
      }); // 🆕 Не фильтруем — передаём все компоненты включая выключенные
    }
    
    return formData.selectedComponents?.length ? formData.selectedComponents : undefined;
  };

  // #BatchTestTemplates: overrides позволяют batch-движку переиспользовать один и тот же
  // запрос для разных тестовых шаблонов и принудительно использовать штатную конфигурацию компонентов.
  const buildGenerationRequest = (overrides?: {
    htmlTemplateOverride?: number;
    forceRecommendedComponents?: boolean;
  }): GenerationRequestV2 => ({
    templateType: formData.templateType,
    htmlTemplate: overrides?.htmlTemplateOverride ?? formData.htmlTemplate!,
    selectedComponents: quickMode || overrides?.forceRecommendedComponents ? undefined : getComponentsToSend(),
    theme: !quickMode && formData.themeMode === 'list' ? formData.theme : undefined,
    themeMode: quickMode ? 'template' : formData.themeMode,
    themeDescription: !quickMode && formData.themeMode === 'custom' ? formData.themeDescription : undefined,
    siteContentTheme: formData.siteContentTheme,
    siteDescriptionOverride: formData.siteDescriptionOverride.trim() || undefined,
    platform: formData.platform,
    geo: formData.geo,
    contentLanguage: formData.contentLanguage,
    legalDocsLanguage: !quickMode ? formData.legalDocsLanguage || undefined : undefined,
    companyName: !quickMode ? formData.companyName.trim() || undefined : undefined,
    companyEmail: !quickMode ? formData.companyEmail.trim() || undefined : undefined,
    companyAddress: !quickMode ? formData.companyAddress.trim() || undefined : undefined,
    companyDomain: !quickMode ? formData.companyDomain.trim() || undefined : undefined,
    advancedMode: !quickMode && formData.advancedMode,
    communicationStyle: !quickMode && formData.advancedMode ? formData.communicationStyle : undefined,
    debugMode: (!quickMode && debugModeActive) || undefined,
    // 🆕 фон в превью/генерации
    backgroundOverride: formData.backgroundEnabled ? {
      enabled: true,
      mode: formData.backgroundMode,
      performance_priority: quickMode ? 'balanced' : formData.backgroundPerformance,
      preset_id: formData.backgroundPresetId,
      curated_id: formData.backgroundCuratedId,
      upload_id: formData.backgroundMode === 'static' ? formData.backgroundUploadId || null : null
    } : { enabled: false },
    // 🆕 Прозрачность компонентов
    componentOpacity: quickMode ? null : formData.componentOpacity,
    // #VisualModernization: preset эффектов секционных фонов (единый на всю сборку)
    componentVisualEffect: quickMode ? 'none' : formData.componentVisualEffect
  });

  const generatePreview = async () => {
    if (!componentsReady || contentError || busy) return;
    const key = formKey;
    const requestId = ++previewRequestRef.current;
    setCheckedForm('');
    try {
      setState(prev => ({ ...prev, previewLoading: true, error: '' }));

      const request = buildGenerationRequest();

      const response = await axios.post('/api/generation/preview-v2', request);
      if (requestId !== previewRequestRef.current || key !== currentFormKeyRef.current) return;
      const previewPayload = response.data.data.preview;
      setCheckedForm(key);
      const previewWarnings = response.data.data.warnings || previewPayload?.warnings || [];
      const templateAccessInfo = response.data.data.templateAccess || previewPayload?.templateAccess || null;
      
      setState(prev => ({
        ...prev,
        preview: previewPayload,
        previewWarnings,
        templateAccess: templateAccessInfo,
        previewTotalComponents: response.data.data.totalComponents || 0,
        previewTotalInstances: response.data.data.totalInstances || 0,
        previewLoading: false
      }));
    } catch (error: unknown) {
      if (requestId !== previewRequestRef.current || key !== currentFormKeyRef.current) return;
      setState(prev => ({
        ...prev,
        error: getApiErrorMessage(error, 'Не удалось проверить конфигурацию. Повторите проверку.'),
        previewLoading: false
      }));
    }
  };

  const pollProgress = async (generationId: string) => {
    try {
      const response = await axios.get(`/api/generation/progress-v2/${generationId}`);
      if (activeGenerationRef.current !== generationId) return;
      const progress = response.data.data as GenerationProgressPayload;
      const progressComponents = Array.isArray(progress.components) ? progress.components : [];
      const queuePosition = typeof progress.queuePosition === 'number' ? progress.queuePosition : null;
      const isQueued = progress.status === 'queued';
      const normalizedStepName = isQueued
        ? (queuePosition ? `В очереди (позиция ${queuePosition})` : (progress.stepName || 'В очереди'))
        : (progress.stepName || '');
      
      // 🆕 Логирование для отладки
      console.log('📊 Прогресс получен:', {
        status: progress.status,
        step: progress.currentStep,
        percentage: progress.percentage,
        componentsCount: progressComponents.length,
        components: progressComponents.map((c) => ({ name: c.name, status: c.status }))
      });
      
      setGenerationProgress({
        status: progress.status || 'processing',
        currentStep: progress.currentStep || 0,
        totalSteps: progress.totalSteps || 7,
        currentComponent: progress.currentComponent || null,
        components: progressComponents,
        componentsCount: progressComponents.length,
        stepName: normalizedStepName,
        percentage: progress.percentage || 0,
        error: progress.error || null,
        queuePosition
      });
      
      // Если генерация завершена или провалилась - останавливаем polling
      if (progress.status === 'completed' || progress.status === 'failed' || progress.status === 'cancelled') {
        stopProgressPolling();
        activeGenerationRef.current = null;
        if (draftUserId) { try { sessionStorage.removeItem(generatorActiveKey(draftUserId, adminMode)); sessionStorage.removeItem(generatorRequestKey(draftUserId, adminMode)); } catch {} }
        
        if (progress.status === 'completed') {
          const generationTimeMs = Number(progress.generationTimeMs || progress.duration || 0);
          const combinedWarnings =
            progress.warnings && progress.warnings.length > 0
              ? progress.warnings
              : state.previewWarnings;
          const availability = progress.availability || state.preview?.availability || null;
          setState(prev => ({
            ...prev,
            generationInProgress: false,
            generationResult: {
              id: progress.dbGenerationId || 0,
              status: 'completed',
              templateUsed: formData.htmlTemplate || 0,
              componentsUsed: progress.componentsCount || progressComponents.length || 0,
              theme: resolvedThemeSlug,
              platform: formData.platform,
              geo: formData.geo,
              contentLanguage: formData.contentLanguage,
              generationTime: generationTimeMs,
              downloadUrl: progress.downloadUrl,
              previewUrl: progress.previewUrl || `/api/generation/preview-v2/${generationId}`,
              warnings: combinedWarnings,
              availability
            }
          }));
        } else {
          setState(prev => ({
            ...prev,
            generationInProgress: false,
            generationResult: null,
            error: progress.error || 'Ошибка генерации сайта'
          }));
        }
      }
      
    } catch (error: unknown) {
      // Если прогресс не найден - продолжаем polling (генерация еще не началась)
      if ([403, 404].includes(getApiErrorStatus(error) || 0)) {
        stopProgressPolling(); activeGenerationRef.current = null;
        if (draftUserId) { try { sessionStorage.removeItem(generatorActiveKey(draftUserId, adminMode)); sessionStorage.removeItem(generatorRequestKey(draftUserId, adminMode)); } catch {} }
        setState(prev => ({...prev,generationInProgress:false,error:'Эта генерация недоступна. Откройте свои сайты в истории.'}));
      }
      if (getApiErrorStatus(error) !== 404) {
        console.error('Ошибка получения прогресса:', error);
      }
    }
  };

  progressRequestRef.current = pollProgress;

  const startGeneration = async () => {
    if (!previewIsCurrent || !componentsReady || contentError || busy || batchInProgress || !canAffordGeneration || generationSubmitRef.current) return;
    generationSubmitRef.current = true;
    try {
      setState(prev => ({ ...prev, generationInProgress: true, error: '', generationResult: null }));
      
      // 🆕 Запускаем таймер генерации
      setGenerationStartTime(Date.now());
      setElapsedSeconds(0);
      setCurrentTipIndex(0);
      
      // 🆕 #F-115: Инициализация прогресса
      setGenerationProgress({
        status: 'processing',
        currentStep: 0,
        totalSteps: 7,
        currentComponent: null,
        components: [] as ProgressComponent[],
        componentsCount: state.recommendedComponents.length || state.preview?.components?.length || 0,
        stepName: 'Запуск генерации...',
        percentage: 0,
        error: null,
        queuePosition: null
      });

      const request = buildGenerationRequest();

      const requestKey = generatorRequestKey(draftUserId, adminMode);
      const requestJson = JSON.stringify(request);
      let operationKey = `ui-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      try {
        const previous = JSON.parse(sessionStorage.getItem(requestKey) || 'null');
        if (previous?.requestJson === requestJson && /^[a-zA-Z0-9_-]{1,128}$/.test(previous.key)) operationKey = previous.key;
        sessionStorage.setItem(requestKey, JSON.stringify({ key: operationKey, requestJson }));
      } catch {}

      // 🆕 #F-115: Отправляем запрос и получаем generationId
      const response = await axios.post('/api/generation/create-v2', request, { headers: { 'Idempotency-Key': operationKey } });
      
      if (response.status === 202 && response.data.data?.generationId) {
        const generationId = response.data.data.generationId;
        activeGenerationRef.current = generationId;
        if (draftUserId) { try { sessionStorage.setItem(generatorActiveKey(draftUserId, adminMode), JSON.stringify({ generationId, startedAt: Date.now() })); } catch {} }
        try { sessionStorage.removeItem(requestKey); } catch {}
        const responseStatus = response.data.data?.status;
        const queuePosition = typeof response.data.data?.queuePosition === 'number'
          ? response.data.data.queuePosition
          : null;

        if (responseStatus === 'queued') {
          setGenerationProgress(prev => ({
            ...prev,
            status: 'queued',
            stepName: queuePosition ? `В очереди (позиция ${queuePosition})` : 'В очереди',
            percentage: 0,
            queuePosition
          }));
        }
        
        // 🆕 #F-115: Запускаем polling каждые 1.5 секунды (setTimeout-цепочка)
        void pollProgress(generationId).finally(() => {
          if (activeGenerationRef.current === generationId) startProgressPolling(() => pollProgress(generationId), 2500);
        });
      } else {
        throw new Error('Не получен generationId от сервера');
      }
      
    } catch (error: unknown) {
      // Останавливаем polling если был запущен
      stopProgressPolling();
      if (draftUserId && [400, 401, 402, 403, 409].includes(getApiErrorStatus(error) || 0)) { try { sessionStorage.removeItem(generatorRequestKey(draftUserId, adminMode)); } catch {} }
      
      setState(prev => ({
        ...prev,
        error: getApiErrorStatus(error) === 402 ? 'Недостаточно средств или доступных генераций. Пополните баланс и повторите запуск — настройки сохранены.' : getApiErrorMessage(error, 'Ошибка генерации сайта'),
        generationInProgress: false
      }));
      
      // Сбрасываем прогресс при ошибке
      setGenerationProgress({
        status: 'failed',
        currentStep: 0,
        totalSteps: 7,
        currentComponent: null,
        components: [] as ProgressComponent[],
        componentsCount: 0,
        stepName: 'Ошибка запуска генерации',
        percentage: 0,
        error: getApiErrorMessage(error, 'Ошибка запуска генерации'),
        queuePosition: null
      });
    } finally { generationSubmitRef.current = false; }
  };
 
  const normalizePreviewUrl = (url?: string) => (url ? url.split('?')[0] : '');
  const buildPreviewUrl = (rawUrl?: string, withDebug = debugModeActive) => {
    const cleanUrl = normalizePreviewUrl(rawUrl);
    if (!cleanUrl) {
      return '';
    }
    if (!withDebug) {
      return cleanUrl;
    }
    const separator = cleanUrl.includes('?') ? '&' : '?';
    return `${cleanUrl}${separator}debug=1`;
  };

  // 🆕 Формат времени для batch-итераций (мм:сс)
  const formatBatchElapsed = (elapsedMs: number | null) => {
    if (!elapsedMs || elapsedMs < 0) {
      return '—';
    }
    const totalSeconds = Math.floor(elapsedMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  // 🆕 Управление раскрытием batch-строк
  const toggleBatchRunExpanded = (index: number) => {
    setBatchExpandedRuns(prev => (
      prev.includes(index) ? prev.filter(item => item !== index) : [...prev, index]
    ));
  };

  const handleBatchRowClick = (event: React.MouseEvent<HTMLTableRowElement>, index: number) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a, button, input, select, textarea')) {
      return;
    }
    toggleBatchRunExpanded(index);
  };

  // 🆕 Рендер подробного прогресса для batch-итерации
  const renderBatchProgressDetails = (run: BatchGenerationRun) => {
    const progress = run.progress;
    const rawComponents = progress?.components;
    const progressComponents = Array.isArray(rawComponents) ? rawComponents : [];
    const orderedProgressComponents = [...progressComponents].sort(
      (a, b) => (a.order || 0) - (b.order || 0)
    );
    const completedCount = orderedProgressComponents.filter((component) => component.status === 'completed').length;
    const currentComponent = run.currentComponent || progress?.currentComponent || null;

    return (
      <div className="batch-progress-panel">
        <div className="batch-progress-header">
          <div className="batch-progress-title">Прогресс генерации</div>
          <div className="batch-progress-percentage">{run.percentage}%</div>
        </div>

        <div className="progress-bar-container">
          <div
            className={`progress-bar-fill ${run.status === 'failed' ? 'error' : ''}`}
            style={{ width: `${run.percentage}%` }}
          ></div>
        </div>

        {run.stepName && (
          <div className="progress-current-step">
            <span className="current-step-icon" aria-hidden="true">
              <ProgressIcon status={run.status} />
            </span>
            <span className="current-step-text">{run.stepName}</span>
          </div>
        )}

        {run.status === 'queued' && (
          <div className="batch-queue-note">
            Очередь: позиция {run.queuePosition ?? '—'}. Ожидание свободного слота (лимиты/нагрузка).
          </div>
        )}

        {run.progressState === 'progress missing' && (
          <div className="batch-progress-missing">
            Прогресс истёк (HTTP 404). Проверьте историю генераций, если результат уже готов.
          </div>
        )}

        {currentComponent && (
          <div className="current-component-indicator">
            <span className="indicator-text">
              Генерируется: {currentComponent.name}
              {currentComponent.variantCode ? ` (${currentComponent.variantCode})` : ''}
              {currentComponent.container ? ` · ${currentComponent.container}` : ''}
              {` (${currentComponent.index}/${currentComponent.total})`}
            </span>
          </div>
        )}

        {orderedProgressComponents.length > 0 && (
          <div className="components-progress-list">
            <h4 className="components-list-title">
              Компоненты ({completedCount}/{orderedProgressComponents.length})
            </h4>
            <div className="components-grid">
              {orderedProgressComponents.map((component) => {
                const variantInfo = component.variantCode ? ` (${component.variantCode})` : '';
                return (
                  <div
                    key={component.id || component.name}
                    className={`component-item component-item--${component.status}`}
                  >
                    <div className="component-order">#{component.order}</div>
                    <div className="component-status-icon">
                      <ProgressIcon status={component.status} />
                    </div>
                    <div className="component-info">
                      <span className="component-name">{component.name}{variantInfo}</span>
                      {component.containerName && (
                        <span className="component-meta">{component.containerName}</span>
                      )}
                      <span className="component-status-text">
                        {component.status === 'completed' && 'Готов'}
                        {component.status === 'processing' && 'Генерируется...'}
                        {component.status === 'failed' && 'Ошибка'}
                        {component.status === 'pending' && 'Ожидание'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {run.error && (
          <div className="progress-error">
            <span className="error-icon" aria-hidden="true">!</span>
            <span className="error-text">{run.error}</span>
          </div>
        )}
      </div>
    );
  };

  const openPreviewInNewTab = async (rawUrl?: string): Promise<boolean> => {
    if (!rawUrl) {
      return false;
    }
    const cleanUrl = normalizePreviewUrl(rawUrl);
    const generationId = extractPreviewGenerationId(cleanUrl);

    // Окно нужно открыть синхронно в том же обработчике клика; после await window.open()
    // часто блокируется как «не пользовательское», из-за чего новая вкладка остаётся пустой.
    // opener обнуляется сразу: превью открывается на другом origin.
    const popup = openPendingPreviewWindow();
    if (!popup) {
      setState(prev => ({
        ...prev,
        error:
          'Браузер заблокировал новое окно. Разрешите всплывающие окна для этого сайта или откройте превью в текущей вкладке.'
      }));
      return false;
    }

    if (!generationId) {
      navigatePreviewWindow(popup, buildPreviewUrl(rawUrl) || cleanUrl);
      return true;
    }

    try {
      // Адрес превью выдаёт сервер; debug уходит в тело запроса.
      const previewUrl = await requestPreviewAccess(generationId, { debug: debugModeActive });
      navigatePreviewWindow(popup, previewUrl);
      return true;
    } catch (error: unknown) {
      popup.close();
      setState(prev => ({
        ...prev,
        error: getApiErrorMessage(error, 'Ошибка открытия превью')
      }));
      return false;
    }
  };

  /** Превью в текущей вкладке: выдаёт доступ и переходит на URL из ответа сервера (удобно для проверки и автотестов). */
  const openPreviewInSameTab = async (rawUrl?: string): Promise<boolean> => {
    if (!rawUrl) {
      return false;
    }
    const cleanUrl = normalizePreviewUrl(rawUrl);
    const generationId = extractPreviewGenerationId(cleanUrl);

    if (!generationId) {
      window.location.assign(buildPreviewUrl(rawUrl) || cleanUrl);
      return true;
    }

    try {
      const previewUrl = await requestPreviewAccess(generationId, { debug: debugModeActive });
      window.location.assign(previewUrl);
      return true;
    } catch (error: unknown) {
      setState(prev => ({
        ...prev,
        error: getApiErrorMessage(error, 'Ошибка открытия превью')
      }));
      return false;
    }
  };

  const updateBatchRun = (index: number, patch: Partial<BatchGenerationRun>) => {
    setBatchRuns(prev => prev.map(run => (run.index === index ? { ...run, ...patch } : run)));
  };

  const startBatchPolling = (generationId: string, runIndex: number, sessionId: number) =>
    new Promise<void>((resolve) => {
      const existingTimer = batchTimersRef.current.get(generationId);
      if (existingTimer) {
        clearTimeout(existingTimer);
        batchTimersRef.current.delete(generationId);
      }

      const poll = async () => {
        if (batchSessionRef.current !== sessionId || batchCancelRef.current) {
          resolve();
          return;
        }

        try {
          const response = await axios.get(`/api/generation/progress-v2/${generationId}`);
          const progress = response.data.data as GenerationProgressPayload;
          const queuePosition = typeof progress.queuePosition === 'number' ? progress.queuePosition : null;
          const isQueued = progress.status === 'queued';
          const normalizedStatus: BatchGenerationStatus =
            progress.status === 'queued'
              ? 'queued'
              : progress.status === 'completed'
                ? 'completed'
                : progress.status === 'failed'
                  ? 'failed'
                  : 'processing';

          const stepName = isQueued
            ? (queuePosition ? `В очереди (позиция ${queuePosition})` : (progress.stepName || 'В очереди'))
            : (progress.stepName || (normalizedStatus === 'completed' ? 'Готово' : normalizedStatus === 'failed' ? 'Ошибка' : ''));
          const percentage = typeof progress.percentage === 'number'
            ? progress.percentage
            : normalizedStatus === 'completed'
              ? 100
              : 0;
          const previewUrl =
            progress.previewUrl || (progress.status === 'completed' ? `/api/generation/preview-v2/${generationId}` : null);
          const downloadUrl = progress.downloadUrl || null;

          updateBatchRun(runIndex, {
            status: normalizedStatus,
            queuePosition,
            previewUrl,
            downloadUrl,
            error: progress.error || null,
            stepName,
            percentage,
            currentComponent: progress.currentComponent || null,
            progress,
            progressState: 'ok'
          });

          if (normalizedStatus === 'completed' || normalizedStatus === 'failed') {
            const timer = batchTimersRef.current.get(generationId);
            if (timer) {
              clearTimeout(timer);
              batchTimersRef.current.delete(generationId);
            }
            const finishedAt = new Date().toISOString();
            setBatchRuns(prev => prev.map(run => {
              if (run.index !== runIndex) {
                return run;
              }
              const elapsedMs = run.startedAt
                ? Math.max(0, new Date(finishedAt).getTime() - new Date(run.startedAt).getTime())
                : run.elapsedMs ?? null;
              return { ...run, finishedAt, elapsedMs };
            }));
            resolve();
            return;
          }
        } catch (error: unknown) {
          const statusCode = getApiErrorStatus(error);
          if (statusCode === 404) {
            setBatchRuns(prev => prev.map(run => {
              if (run.index !== runIndex) {
                return run;
              }
              if (run.progressState === 'progress missing') {
                return run;
              }
              return { ...run, progressState: 'progress missing' };
            }));
          } else {
            console.error('Ошибка получения прогресса batch:', error);
          }
        }

        const timer = setTimeout(() => {
          void poll();
        }, 1500);
        batchTimersRef.current.set(generationId, timer);
      };

      void poll();
    });

  const startBatchRun = async (
    index: number,
    sessionId: number,
    target: { templateId: number; templateName: string },
    options?: { forceRecommendedComponents?: boolean }
  ) => {
    if (batchSessionRef.current !== sessionId || batchCancelRef.current) {
      return;
    }

    const startedAt = new Date().toISOString();
    setBatchRuns(prev => ([
      ...prev,
      {
        index,
        templateId: target.templateId,
        templateName: target.templateName,
        status: 'processing',
        generationId: null,
        queuePosition: null,
        previewUrl: null,
        downloadUrl: null,
        error: null,
        startedAt,
        finishedAt: null,
        elapsedMs: 0,
        stepName: 'Запуск генерации...',
        percentage: 0,
        currentComponent: null,
        progress: null,
        progressState: 'ok'
      }
    ]));

    try {
      const response = await axios.post(
        '/api/generation/create-v2',
        buildGenerationRequest({
          htmlTemplateOverride: target.templateId,
          forceRecommendedComponents: options?.forceRecommendedComponents === true
        })
      );

      if (batchSessionRef.current !== sessionId || batchCancelRef.current) {
        return;
      }

      if (response.status === 202 && response.data.data?.generationId) {
        const generationId = response.data.data.generationId as string;
        const responseStatus = response.data.data?.status;
        const queuePosition = typeof response.data.data?.queuePosition === 'number'
          ? response.data.data.queuePosition
          : null;
        const status: BatchGenerationStatus = responseStatus === 'queued' ? 'queued' : 'processing';
        const stepName = status === 'queued'
          ? (queuePosition ? `В очереди (позиция ${queuePosition})` : 'В очереди')
          : 'Генерация...';

        updateBatchRun(index, {
          generationId,
          status,
          queuePosition,
          stepName,
          percentage: 0
        });

        await startBatchPolling(generationId, index, sessionId);
        return;
      }

      throw new Error('Не получен generationId от сервера');
    } catch (error: unknown) {
      const finishedAt = new Date().toISOString();
      updateBatchRun(index, {
        status: 'failed',
        error: getApiErrorMessage(error, 'Ошибка запуска генерации'),
        finishedAt,
        stepName: 'Ошибка запуска',
        percentage: 0
      });
      setBatchRuns(prev => prev.map(run => {
        if (run.index !== index) {
          return run;
        }
        const elapsedMs = run.startedAt
          ? Math.max(0, new Date(finishedAt).getTime() - new Date(run.startedAt).getTime())
          : run.elapsedMs ?? null;
        return { ...run, elapsedMs };
      }));
    }
  };

  // #BatchTestTemplates: формирует список целей batch-прогона.
  // Если выбрано ≥2 тестовых шаблонов — по одному прогону на каждый выбранный.
  // Иначе — N копий одного выбранного шаблона (обычный режим).
  const getBatchTargets = (): Array<{ templateId: number; templateName: string }> => {
    if (isBatchTestMode) {
      return selectedTestTemplateIds
        .map(id => templateCollections.testTemplates.find(tpl => tpl.id === id))
        .filter((tpl): tpl is NonNullable<typeof tpl> => Boolean(tpl))
        .map(tpl => ({
          templateId: tpl.id,
          templateName: tpl.name
        }));
    }
    if (!formData.htmlTemplate) {
      return [];
    }
    const tpl = state.htmlTemplates.find(t => t.id === formData.htmlTemplate);
    const templateId = formData.htmlTemplate;
    const templateName = tpl?.name || `Шаблон #${templateId}`;
    return Array.from({ length: batchCount }, () => ({ templateId, templateName }));
  };

  const startBatchGeneration = async () => {
    if (!isAdmin || batchInProgress) {
      return;
    }

    const targets = getBatchTargets();
    if (targets.length === 0) {
      setState(prev => ({
        ...prev,
        error: isBatchTestMode
          ? 'Нет тестовых шаблонов для текущего типа сайта'
          : 'Выберите HTML шаблон'
      }));
      return;
    }

    setBatchInProgress(true);
    setBatchPreviewBlockedUrls([]);
    setBatchDownloadBlockedUrls([]);
    clearBatchTimers();
    batchCancelRef.current = false;
    batchSessionRef.current += 1;

    const sessionId = batchSessionRef.current;
    const maxIndex = batchRuns.reduce((max, run) => Math.max(max, run.index), 0);
    const indices = targets.map((_, i) => maxIndex + i + 1);
    // #BatchTestTemplates: для batch-test режима (≥2 тестовых шаблонов) всегда последовательный запуск,
    // parallel недопустим по требованию задачи. И компоненты не редактируются — всегда рекомендованные.
    const effectiveMode: 'sequential' | 'parallel' = isBatchTestMode ? 'sequential' : batchMode;
    const forceRecommendedComponents = isBatchTestMode;

    if (effectiveMode === 'parallel') {
      await Promise.allSettled(
        indices.map((index, i) =>
          startBatchRun(index, sessionId, targets[i], { forceRecommendedComponents })
        )
      );
    } else {
      for (let i = 0; i < indices.length; i++) {
        if (batchSessionRef.current !== sessionId || batchCancelRef.current) {
          break;
        }
        await startBatchRun(indices[i], sessionId, targets[i], { forceRecommendedComponents });
      }
    }

    if (batchSessionRef.current === sessionId) {
      setBatchInProgress(false);
    }
  };

  const resetBatchRuns = () => {
    batchSessionRef.current += 1;
    clearBatchTimers();
    setBatchRuns([]);
    setBatchInProgress(false);
    setBatchPreviewBlockedUrls([]);
    setBatchDownloadBlockedUrls([]);
    setBatchExpandedRuns([]);
  };

  const batchPreviewUrls = useMemo(() => {
    const urls = batchRuns
      .map(run => run.previewUrl)
      .filter((url): url is string => Boolean(url));
    return Array.from(new Set(urls));
  }, [batchRuns]);

  const batchDownloadUrls = useMemo(() => {
    const urls = batchRuns
      .map(run => run.downloadUrl)
      .filter((url): url is string => Boolean(url));
    return Array.from(new Set(urls));
  }, [batchRuns]);

  const openAllBatchPreviews = async () => {
    if (batchPreviewUrls.length === 0) {
      return;
    }
    setBatchPreviewBlockedUrls([]);

    const blocked: string[] = [];
    for (const url of batchPreviewUrls) {
      const opened = await openPreviewInNewTab(url);
      if (!opened) {
        blocked.push(normalizePreviewUrl(url));
      }
      await new Promise(resolve => setTimeout(resolve, 180));
    }

    if (blocked.length > 0) {
      setBatchPreviewBlockedUrls(blocked);
    }
  };

  const downloadAllBatchSites = async () => {
    if (batchDownloadUrls.length === 0) {
      return;
    }
    setBatchDownloadBlockedUrls([]);

    const blocked: string[] = [];
    for (const url of batchDownloadUrls) {
      const downloadUrl = addTokenToUrl(url);
      const opened = window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      if (!opened) {
        blocked.push(url);
      }
      await new Promise(resolve => setTimeout(resolve, 260));
    }

    if (blocked.length > 0) {
      setBatchDownloadBlockedUrls(blocked);
    }
  };

  // 🆕 #F-115: Очистка polling при размонтировании
  useEffect(() => {
    return () => {
      stopProgressPolling();
      activeGenerationRef.current = null;
    };
  }, [stopProgressPolling]);

  const { regularTemplates, smokeTemplates, testTemplates } = templateCollections;

  const filteredRegularTemplates = useMemo(() => {
    const q = templateSearch.trim().toLowerCase();
    return regularTemplates
      .filter((t) => {
        if (templateColumnsFilter === 'all') return true;
        return String(getTemplateColumnsCount(t)) === templateColumnsFilter;
      })
      .filter((t) => {
        if (!q) return true;
        const name = String(t.name || '').toLowerCase();
        const desc = String(t.description || '').toLowerCase();
        const comps = getTemplateComponents(t);
        const compText = [...comps.required, ...comps.optional].join(' ').toLowerCase();
        return name.includes(q) || desc.includes(q) || compText.includes(q);
      });
  }, [regularTemplates, templateColumnsFilter, templateSearch]);

  const stepperItems = useMemo(
    () =>
      [
        { step: 1 as GeneratorStep, label: 'Шаблон', hint: 'превью • выбор' },
        { step: 2 as GeneratorStep, label: 'Оформление', hint: 'цвета и шрифты' },
        { step: 3 as GeneratorStep, label: 'Контент', hint: 'тематика • настройки текста' },
        { step: 4 as GeneratorStep, label: 'Блоки', hint: 'состав сайта' },
        { step: 5 as GeneratorStep, label: 'Язык и регион', hint: 'язык • документы' },
        { step: 6 as GeneratorStep, label: 'Фон', hint: 'глобальный фон' },
        { step: 7 as GeneratorStep, label: 'Проверка', hint: 'итог • запуск' }
      ] as const,
    []
  );

  const handleGoToCompletedStep = useCallback((step: GeneratorStep) => {
    if (busy || batchInProgress || (step > 1 && !formData.htmlTemplate)) return;
    if (quickMode && (step === 2 || step === 4)) return;
    setCurrentStep(step);
  }, [busy, batchInProgress, formData.htmlTemplate, quickMode]);

  const changeInterfaceMode = (mode: 'quick' | 'full') => {
    if (busy || batchInProgress) return;
    setInterfaceMode(mode);
    if (mode === 'quick' && (currentStep === 2 || currentStep === 4)) setCurrentStep(3);
    if (mode === 'quick') setSelectedTestTemplateIds([]);
    navigate(`${adminMode ? "/admin/generator" : "/generator-v2"}?mode=${mode}`, { replace: true });
  };

  if (state.loading) {
    return (
      <section className="u2-card is-gradient">
        <div className="u2-cardInner">
          <div className="u2-strong">Загрузка генератора…</div>
          <div className="u2-muted" style={{ fontSize: 12, marginTop: 6 }}>Пожалуйста, подождите.</div>
        </div>
      </section>
    );
  }

  return (
    <div className={`u2-grid generator-v2-container ${quickMode ? 'is-quick' : ''}`} style={{ gap: 18 }}>
      {isAdmin && <section className="u2-card is-soft" aria-label="Тестовая генерация">
        <div className="u2-cardInner">
          <h1 className="u2-title">Тестовая генерация</h1>
          <p className="u2-subtitle">Тестовые шаблоны, пакетные запуски и отладка доступны здесь. Готовые сайты сохраняются в истории вашего аккаунта.</p>
        </div>
      </section>}
      <div className="wp-modeSwitch" role="group" aria-label="Режим создания сайта">
        <button type="button" className={`u2-btn ${quickMode ? 'is-success' : 'is-secondary'}`} aria-pressed={quickMode} disabled={busy || batchInProgress} onClick={() => changeInterfaceMode('quick')}>Быстро и просто</button>
        <button type="button" className={`u2-btn ${!quickMode ? 'is-success' : 'is-secondary'}`} aria-pressed={!quickMode} disabled={busy || batchInProgress} onClick={() => changeInterfaceMode('full')}>Все настройки</button>
      </div>
      {draftUserId && formData.htmlTemplate && <div className="u2-row" style={{ justifyContent: 'space-between', gap: 12 }}>
        <span className="u2-muted" role="status">{draftRestored ? 'Черновик восстановлен. Проверьте настройки перед запуском.' : 'Настройки сохраняются в этой вкладке.'}</span>
        <button type="button" className="u2-btn" disabled={busy || batchInProgress} onClick={() => {
          setFormData(initialFormRef.current); setCurrentStep(1); setFurthestStep(1); setDraftRestored(false);
          setGenerationProgress(prev => ({ ...prev, error: null })); setState(prev => ({ ...prev, error: '' }));
        }}>Начать заново</button>
      </div>}
      <GeneratorV2HeaderCard currentStep={visibleSteps.indexOf(currentStep) + 1} totalSteps={visibleSteps.length} />
      <GeneratorV2Stepper
        items={stepperItems.filter(item => visibleSteps.includes(item.step))}
        currentStep={currentStep}
        furthestStep={furthestStep}
        disabled={busy || batchInProgress}
        onSelectCompletedStep={handleGoToCompletedStep}
      />

      <section ref={workspaceRef} className={`u2-card u2-wizardWorkspace ${currentStep === 6 ? 'u2-generatorWorkspace' : ''}`}>
        <div className="u2-cardInner">
          {/* Основной контент */}
          <fieldset className="generator-content u2-generatorFields" disabled={busy || batchInProgress} style={{ padding: 0 }}>
        {displayedError && (
          <div className="error-message" role="alert">
            <span className="error-icon" aria-hidden="true">!</span>
            {displayedError}
            {!state.htmlTemplates.length && <button className="u2-btn is-secondary" type="button" onClick={loadInitialData}>Повторить загрузку</button>}
          </div>
        )}

        {componentLoad.loading && <p role="status">Загружаем блоки выбранного шаблона…</p>}
        {componentLoad.error && <div className="error-message" role="alert">{componentLoad.error} <button type="button" className="u2-btn is-secondary" onClick={() => formData.htmlTemplate && loadTemplateComponents(formData.htmlTemplate)}>Повторить загрузку блоков</button></div>}
        {currentStep === 6 && backgroundLoadError && <div className="error-message" role="alert">{backgroundLoadError}<button type="button" className="u2-btn is-secondary" onClick={() => setBackgroundLoadVersion(v => v + 1)}>Повторить загрузку фонов</button></div>}
        {/* Шаг 1: Выбор шаблона */}
        {currentStep === 1 && (
          <div className="step-content">
            <div className="u2-cardHeader">
              <div>
                <h2 className="u2-title">Шаг 1: Выбор шаблона</h2>
                <p className="u2-subtitle">
                  Выберите тип сайта и шаблон для генерации.
                </p>
              </div>
            </div>

            {/* 🆕 Переключатель типа сайта — в самом верху */}
            <div className="u2-siteTypeSection">
              <div className="u2-siteTypeToggle is-prominent">
                <button
                  type="button"
                  className={`u2-siteTypeBtn is-landing ${formData.siteType === 'landing' ? 'is-active' : ''}`}
                  onClick={() => handleSiteTypeChange('landing')}
                >
                  <span className="u2-siteTypeIcon">📄</span>
                  <span className="u2-siteTypeName">Лендинг</span>
                  <span className="u2-siteTypeHint">Одностраничный сайт</span>
                </button>
                <button
                  type="button"
                  className={`u2-siteTypeBtn is-multipage ${formData.siteType === 'multipage' ? 'is-active' : ''}`}
                  onClick={() => handleSiteTypeChange('multipage')}
                >
                  <span className="u2-siteTypeIcon">📑</span>
                  <span className="u2-siteTypeName">Многостраничный</span>
                  <span className="u2-siteTypeHint">Несколько страниц</span>
                </button>
              </div>
            </div>

            {selectedTemplate && (
              <div className="u2-selectionSummary" role="status">
                <div><strong>Выбран: {selectedTemplate.name}</strong><p className="u2-muted">{selectedTemplateColumnsCount} кол. · {selectedTemplateComponents.total} типов блоков. Состав можно настроить на шаге 4.</p></div>
                <button type="button" className="u2-btn is-secondary" onClick={handleStepNext} disabled={nextDisabled}>Продолжить →</button>
              </div>
            )}
            <p className="u2-muted">Сравните состав блоков и число колонок. Схема условная; порядок уточняется на шаге 4. Отмеченные пунктиром блоки — дополнительные; точный вид зависит от выбранных вариантов.</p>

            <div className="u2-toolbar">
              <div className="u2-toolbarGroup">
                <div className="u2-field" style={{ minWidth: 'min(340px, 100%)' }}>
                  <div className="u2-label">Поиск шаблонов</div>
                  <input
                    className="u2-input"
                    aria-label="Поиск шаблонов"
                    value={templateSearch}
                    onChange={(e) => setTemplateSearch(e.target.value)}
                    placeholder="Название, описание или тип блока"
                  />
                </div>
                <div className="u2-field">
                  <div className="u2-label">Колонки</div>
                  <div className="u2-segment">
                    <button
                      type="button"
                      className={templateColumnsFilter === 'all' ? 'is-active' : ''}
                      onClick={() => setTemplateColumnsFilter('all')}
                    >
                      Все
                    </button>
                    <button
                      type="button"
                      className={templateColumnsFilter === '1' ? 'is-active' : ''}
                      onClick={() => setTemplateColumnsFilter('1')}
                    >
                      1
                    </button>
                    <button
                      type="button"
                      className={templateColumnsFilter === '2' ? 'is-active' : ''}
                      onClick={() => setTemplateColumnsFilter('2')}
                    >
                      2
                    </button>
                    <button
                      type="button"
                      className={templateColumnsFilter === '3' ? 'is-active' : ''}
                      onClick={() => setTemplateColumnsFilter('3')}
                    >
                      3
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="u2-divider" />

            <div className="u2-tplGrid" aria-label="Список HTML шаблонов">
              {filteredRegularTemplates.map(renderTemplateCard)}
              {filteredRegularTemplates.length === 0 ? (
                <div className="u2-muted" style={{ fontSize: 12 }}>
                  Шаблоны не найдены. Попробуйте другое название или сбросьте фильтры.
                  <button type="button" className="u2-btn is-secondary" onClick={() => { setTemplateSearch(''); setTemplateColumnsFilter('all'); }}>Сбросить фильтры</button>
                </div>
              ) : null}
            </div>

            {isAdmin && !quickMode && testTemplates.length > 0 ? (
              <>
                <div className="u2-divider" />
                <div className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
                  <div className="u2-cardInner">
                    <div className="u2-row" style={{ alignItems: 'flex-start' }}>
                      <div style={{ minWidth: 0 }}>
                        <div className="u2-strong">Тестовые шаблоны (для проверки компонентов)</div>
                        <div className="u2-muted" style={{ fontSize: 12, marginTop: 6 }}>
                          Доступно только администратору. Используются для проверки покрытия компонентов.
                        </div>
                      </div>
                      <button type="button" className="u2-btn is-secondary" onClick={() => setShowTestTemplates((prev) => !prev)}>
                        {showTestTemplates ? 'Скрыть' : 'Показать'}
                      </button>
                    </div>

                    {/* #BatchTestTemplates: master-чекбокс «все» + список с индивидуальными чекбоксами.
                        Поведение: 0 выбрано — обычный режим. 1 выбран — обычный режим с этим шаблоном
                        (компоненты редактируемы). ≥2 — batch-test режим: последовательный запуск,
                        компоненты не редактируются. */}
                    <label
                      className="u2-row"
                      style={{ justifyContent: 'flex-start', gap: 10, marginTop: 12, cursor: 'pointer' }}
                    >
                      <input
                        type="checkbox"
                        checked={isAllTestTemplatesSelected}
                        onChange={(e) => toggleAllTestTemplates(e.target.checked)}
                      />
                      <span>
                        <span className="u2-strong">Выбрать все тестовые шаблоны текущего типа</span>
                        <br />
                        <span className="u2-muted" style={{ fontSize: 12 }}>
                          {`Всего доступно ${testTemplates.length} тестовых шаблонов типа ${formData.siteType}. `}
                          При выборе 2 и более — общие настройки задаются один раз,
                          список компонентов каждого шаблона не редактируется, запуск — последовательный.
                        </span>
                      </span>
                    </label>

                    {/* Список тестовых шаблонов с индивидуальными чекбоксами */}
                    <div style={{ marginTop: 12, display: 'grid', gap: 6 }}>
                      {testTemplates.map(tpl => {
                        const isChecked = selectedTestTemplateIds.includes(tpl.id);
                        return (
                          <label
                            key={tpl.id}
                            className="u2-row"
                            style={{
                              justifyContent: 'flex-start',
                              gap: 10,
                              cursor: 'pointer',
                              padding: '6px 10px',
                              borderRadius: 8,
                              background: isChecked ? 'rgba(59, 130, 246, 0.08)' : 'transparent'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => toggleTestTemplateSelected(tpl.id)}
                            />
                            <span style={{ fontSize: 13 }}>{tpl.name}</span>
                          </label>
                        );
                      })}
                    </div>

                    {selectedTestTemplateIds.length > 0 ? (
                      <div className="u2-muted" style={{ fontSize: 12, marginTop: 10 }}>
                        {selectedTestTemplateIds.length === 1
                          ? 'Выбран 1 шаблон — доступна настройка компонентов.'
                          : `Выбрано ${selectedTestTemplateIds.length} шаблонов — batch-test режим, компоненты не редактируются.`}
                      </div>
                    ) : null}
                  </div>
                </div>
              </>
            ) : null}

            {isAdmin && !quickMode && smokeTemplates.length > 0 ? (
              <>
                <div className="u2-divider" />
                <div className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
                  <div className="u2-cardInner">
                    <div className="u2-row" style={{ alignItems: 'flex-start' }}>
                      <div style={{ minWidth: 0 }}>
                        <div className="u2-strong">Smoke‑шаблоны (для отладки)</div>
                        <div className="u2-muted" style={{ fontSize: 12, marginTop: 6 }}>
                          Доступно только администратору. Отдельные страницы для групп компонентов.
                        </div>
                      </div>
                      <button type="button" className="u2-btn is-secondary" onClick={() => setShowSmokeTemplates((prev) => !prev)}>
                        {showSmokeTemplates ? 'Скрыть' : 'Показать'}
                      </button>
                    </div>

                    {showSmokeTemplates ? (
                      <div className="u2-tplGrid" style={{ marginTop: 12 }}>
                        {smokeTemplates.map(renderTemplateCard)}
                      </div>
                    ) : null}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        )}

        {/* Шаг 2: Тема оформления */}
        {currentStep === 2 && (
          <div className="step-content">
            <div className="u2-cardHeader">
              <div>
                <h2 className="u2-title">Шаг 2: Тема оформления</h2>
                <p className="u2-subtitle">Оформление задаёт цвета и шрифты будущего сайта. Тематику текстов вы выберете на следующем шаге.</p>
              </div>
              <span className="u2-pill is-warn">обязательный</span>
            </div>

            <div className="u2-grid cols-2" style={{ alignItems: 'start' }}>
              <div className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
                <div className="u2-cardInner">
                  <div className="u2-label">Как выбираем тему</div>
                  <div className="u2-modeGrid" style={{ marginTop: 10 }}>
                    <div
                      className={`u2-modeCard ${formData.themeMode === 'template' ? 'is-selected' : ''}`}
                      onClick={() => handleThemeModeChange('template')}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleThemeModeChange('template');
                        }
                      }}
                    >
                      <div className="u2-modeTitle">Темы шаблона</div>
                      <div className="u2-modeHint">Автоматически по шаблону</div>
                    </div>
                    <div
                      className={`u2-modeCard ${formData.themeMode === 'list' ? 'is-selected' : ''}`}
                      onClick={() => handleThemeModeChange('list')}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleThemeModeChange('list');
                        }
                      }}
                    >
                      <div className="u2-modeTitle">Из библиотеки</div>
                      <div className="u2-modeHint">Выбор темы вручную</div>
                    </div>
                  </div>

                  {formData.themeMode === 'template' ? (
                    <div style={{ marginTop: 12 }}>
                      <div className="u2-card" style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}>
                        <div className="u2-cardInner">
                          <div className="u2-row">
                            <span className="u2-muted">Рекомендуемая</span>
                            <span className="u2-badge">
                              {currentTemplateTheme ? getThemeName(currentTemplateTheme) : 'Подбор при генерации'}
                            </span>
                          </div>
                          <div className="u2-row" style={{ marginTop: 8 }}>
                            <span className="u2-muted">Допустимые</span>
                            <span className="u2-muted" style={{ fontSize: 12 }}>
                              {currentTemplateAllowedThemes.length > 0
                                ? currentTemplateAllowedThemes.map((slug) => getThemeName(slug)).join(', ')
                                : 'Без ограничений шаблона'}
                            </span>
                          </div>
                          <div className="u2-muted" style={{ fontSize: 12, marginTop: 10 }}>
                            {readString(currentTemplateMetadata.themeDescription) || 'Чтобы получить определённое оформление, выберите тему из библиотеки.'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {formData.themeMode === 'list' ? (
                    <div style={{ marginTop: 12 }}>
                      {/* Переключатель Светлые / Тёмные */}
                      <div className="u2-modeGrid" style={{ marginBottom: 10 }}>
                        <div
                          className={`u2-modeCard ${themePalette === 'light' ? 'is-selected' : ''}`}
                          onClick={() => setThemePalette('light')}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setThemePalette('light');
                            }
                          }}
                        >
                          <div className="u2-modeTitle">Светлые</div>
                          <div className="u2-modeHint">{state.themes.filter(t => !t.isDark).length} тем</div>
                        </div>
                        <div
                          className={`u2-modeCard ${themePalette === 'dark' ? 'is-selected' : ''}`}
                          onClick={() => setThemePalette('dark')}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setThemePalette('dark');
                            }
                          }}
                        >
                          <div className="u2-modeTitle">Тёмные</div>
                          <div className="u2-modeHint">{state.themes.filter(t => t.isDark).length} тем</div>
                        </div>
                      </div>
                      <div className="u2-themeGrid">
                        {state.themes
                          .filter(theme => themePalette === 'dark' ? theme.isDark : !theme.isDark)
                          .map((theme) => {
                            const isSelected = formData.theme === theme.cssClass;
                            return (
                              <div
                                key={theme.id}
                                className={`u2-themeCard ${isSelected ? 'is-selected' : ''}`}
                                onClick={() => handleFormChange('theme', theme.cssClass)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleFormChange('theme', theme.cssClass);
                                  }
                                }}
                              >
                                <ThemeExample theme={theme} compact />
                                <div className="u2-themeBody">
                                  <div className="u2-themeName">{theme.name.replace(/^[^A-Za-zА-Яа-я0-9]+/, '')}</div>
                                  <div className="u2-themeMeta">{theme.description}</div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  ) : null}

                </div>
              </div>

              <div className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
                <div className="u2-cardInner">
                  <div className="u2-card" style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}>
                    <div className="u2-cardInner">
                      <div className="u2-strong">Пример оформления</div>
                      <p className="u2-muted">Палитра выбранной темы на условном макете. Состав и тексты будут соответствовать вашим настройкам.</p>
                      <ThemeExample theme={state.themes.find(t => t.cssClass === (formData.themeMode === 'template' ? currentTemplateTheme : formData.theme))} />
                      <div className="u2-grid" style={{ marginTop: 10 }}>
                        <div className="u2-row">
                          <span className="u2-muted">Шаблон</span>
                          <span className="u2-badge">{selectedTemplate ? selectedTemplate.name : '—'}</span>
                        </div>
                        <div className="u2-row">
                          <span className="u2-muted">Тема</span>
                          <span className="u2-badge">{formData.themeMode === 'template' ? 'По правилам шаблона' : getThemeName(formData.theme)}</span>
                        </div>
                        <div className="u2-row">
                          <span className="u2-muted">Тематика</span>
                          <span className="u2-badge">{selectedContentTheme ? selectedContentTheme.label : '—'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="u2-divider" />
                  <div className="u2-muted" style={{ fontSize: 12 }}>
                    Дальше: <span className="u2-strong">тематика контента</span> + <span className="u2-strong">расширенные настройки</span>.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Шаг 3: О чём ваш сайт */}
        {currentStep === 3 && (
          <div className="step-content wp-contentStep">
            <div className="u2-cardHeader">
              <div>
                <h2 className="u2-title">Шаг {visibleSteps.indexOf(3) + 1}: О чём ваш сайт</h2>
                <p className="u2-subtitle">{quickMode ? 'Выберите тематику. При желании добавьте короткое описание.' : 'Задаём тематику, описание сайта и (опционально) данные компании. Это влияет на тексты и структуру контента.'}</p>
              </div>
              <span className="u2-pill is-warn">обязательный</span>
            </div>

            <div className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent', marginBottom: 14 }}>
              <div className="u2-cardInner">
                <div className="u2-row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div>
                    <div className="u2-strong">Тематика контента</div>
                    <div className="u2-muted" style={{ fontSize: 12 }}>
                      Определяет стиль текстов и подбор изображений.
                    </div>
                  </div>
                  <button type="button" aria-label="Пояснение" className="u2-info" tabIndex={0} data-tip="Тематика влияет на стиль текстов, подбор изображений и акценты в контенте.">
                    i
                  </button>
                </div>

                <select
                  className="u2-select"
                  style={{ marginTop: 10 }}
                  aria-label="Тематика сайта"
                            value={formData.siteContentTheme}
                  onChange={(e) => handleFormChange('siteContentTheme', e.target.value)}
                >
                  {state.contentThemes.map((theme) => (
                    <option key={theme.key} value={theme.key}>
                      {theme.label}
                    </option>
                  ))}
                </select>
                <div className="u2-muted" style={{ fontSize: 12, marginTop: 10 }}>
                  {selectedContentTheme?.description || ''}
                </div>
              </div>
            </div>

            <div className="u2-field wp-companyNameField" style={{ margin: '18px 0' }}>
              <label className="u2-label" htmlFor="company-name">Название компании</label>
              <input id="company-name" className="u2-input" maxLength={150}
                value={formData.companyName} onChange={(e) => handleFormChange('companyName', e.target.value)}
                placeholder="Необязательно — можно придумать автоматически" />
              <p className="u2-muted">Укажите точное название, если его нужно сохранить на всех страницах.</p>
            </div>
            <div className="u2-field" style={{ margin: '18px 0' }}>
              <label className="u2-label" htmlFor="site-description">Описание сайта</label>
              <textarea id="site-description" value={formData.siteDescriptionOverride}
                onChange={(e) => handleFormChange('siteDescriptionOverride', e.target.value)}
                className="u2-input" maxLength={800} style={{ minHeight: 110, resize: 'vertical' }}
                placeholder="Уточните тематику сайта. Это повлияет на тексты и изображения." />
              <p className="u2-muted">Необязательно. Опишите предложение, аудиторию и особенности, которые нужно отразить на сайте.</p>
            </div>
            <div className="u2-card is-soft wp-advancedOptions" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
              <div className="u2-cardInner">
                <label className="u2-row" style={{ justifyContent: 'flex-start', gap: 10, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.advancedMode}
                    onChange={(e) => handleFormChange('advancedMode', e.target.checked)}
                  />
                  <span>
                    <span className="u2-strong">Расширенные настройки</span>
                    <br />
                    <span className="u2-muted" style={{ fontSize: 12 }}>
                      Стиль общения и данные компании.
                    </span>
                  </span>
                </label>

                {formData.advancedMode ? (
                  <div style={{ marginTop: 12 }}>
                    <div className="u2-grid cols-2 wp-contentFields" style={{ alignItems: 'start' }}>
                      <div className="u2-grid wp-descriptionFields">
                        <div className="u2-strong">Тексты сайта</div>
                        <div className="u2-field">
                          <div className="u2-label">Стиль коммуникации</div>
                          <select
                            className="u2-select"
                            aria-label="Стиль общения"
                            value={formData.communicationStyle}
                            onChange={(e) => handleFormChange('communicationStyle', e.target.value)}
                          >
                            <option value="professional">Профессиональный</option>
                            <option value="friendly">Дружелюбный</option>
                            <option value="aggressive">Агрессивный</option>
                            <option value="minimal">Минималистичный</option>
                            <option value="creative">Креативный</option>
                          </select>
                        </div>
                      </div>
                      <div className="u2-grid">
                        <div className="u2-strong" style={{ fontSize: 13 }}>
                          Данные компании (опционально)
                        </div>
                        <p className="u2-muted">Заполнять необязательно. Пустые данные будут сгенерированы; адрес — с учётом выбранных языка и региона. Если указан домен, email по умолчанию будет info@ваш-домен.</p>
                        <div className="u2-field">
                          <label className="u2-label" htmlFor="company-domain">Домен</label>
                          <input id="company-domain" className="u2-input" type="text" value={formData.companyDomain} maxLength={253} onChange={event => handleFormChange('companyDomain', event.target.value)} placeholder="example.com" />
                        </div>
                        <div className="u2-field">
                          <div className="u2-label">Email</div>
                          <input
                            className="u2-input"
                            type="email"
                            aria-label="Email компании"
                            value={formData.companyEmail}
                            onChange={(e) => handleFormChange('companyEmail', e.target.value)}
                            placeholder="legal@example.com"
                          />
                        </div>
                        <div className="u2-field">
                          <div className="u2-label">Адрес</div>
                          <input
                            className="u2-input"
                            aria-label="Адрес компании"
                            value={formData.companyAddress}
                            onChange={(e) => handleFormChange('companyAddress', e.target.value)}
                            placeholder="Город, улица, дом"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}

        {(currentStep === 3 || currentStep === 7) && contentError && <p role="alert" className="error-message">{contentError} {currentStep === 7 && <button type="button" className="u2-btn is-secondary" onClick={() => handleGoToCompletedStep(3)}>Исправить данные сайта</button>}</p>}
        {/* Шаг 4: Блоки сайта (v5) */}
        {currentStep === 4 && (
          <div className="step-content">
            <div className="u2-cardHeader">
              <div>
                <h2 className="u2-title">Шаг 4: Блоки сайта</h2>
                <p className="u2-subtitle">
                  Выберите, какие блоки войдут в сайт и как они будут выглядеть. Можно оставить автоматический подбор.
                </p>
              </div>
              <span className="u2-pill">опционально</span>
            </div>

            {/* #BatchTestTemplates: в режиме пакетного прогона тестовых шаблонов
                список компонентов не редактируется — для каждого шаблона используется
                его штатная конфигурация, одинаковые overrides переносить нельзя. */}
            {runAllTestTemplates ? (
              <div className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent', marginBottom: 14 }}>
                <div className="u2-cardInner">
                  <div className="u2-row" style={{ alignItems: 'flex-start', gap: 12 }}>
                    <span className="u2-pill is-warn">Batch test</span>
                    <div style={{ minWidth: 0 }}>
                      <div className="u2-strong" style={{ fontSize: 13 }}>
                        Для пакетного запуска тестовых шаблонов список компонентов не редактируется.
                      </div>
                      <div className="u2-muted" style={{ fontSize: 12, marginTop: 6 }}>
                        Для каждого шаблона будет использована его штатная конфигурация.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent', marginBottom: 14 }}>
                <div className="u2-cardInner">
                  <label className="u2-row" style={{ justifyContent: 'flex-start', gap: 10, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!formData.useRecommendedComponents}
                      onChange={(e) => handleFormChange('useRecommendedComponents', !e.target.checked)}
                    />
                    <span>
                      <span className="u2-strong">Настроить компоненты</span>
                      <br />
                      <span className="u2-muted" style={{ fontSize: 12 }}>
                        Выключено — варианты подбираются по правилам шаблона, дополнительные блоки могут быть добавлены случайно. Включите, чтобы задать состав самостоятельно.
                      </span>
                    </span>
                  </label>
                </div>
              </div>
            )}

            {!runAllTestTemplates && <p className="u2-muted u2-blockExplanation">Обязательные блоки включаются всегда. Для дополнительных выберите «Включить», «Исключить» или «Автовыбор». Вариант определяет вид блока.</p>}

            {!runAllTestTemplates && (() => {
              const customizing = !formData.useRecommendedComponents;

              if (!formData.htmlTemplate) {
                return (
                  <div className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
                    <div className="u2-cardInner">
                      <div className="u2-muted" style={{ fontSize: 12 }}>
                        Сначала выберите шаблон на шаге 1.
                      </div>
                    </div>
                  </div>
                );
              }

              if (!state.recommendedComponents || state.recommendedComponents.length === 0) {
                return (
                  <div className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
                    <div className="u2-cardInner">
                      <div className="u2-muted" style={{ fontSize: 12 }}>
                        Нет данных структуры компонентов (проверьте шаблон или доступность API).
                      </div>
                    </div>
                  </div>
                );
              }

              const sectionOf = (type: unknown): 'header' | 'main' | 'footer' => {
                const t = String(type || '').toLowerCase();
                if (t.includes('header') || t.includes('navigation') || t.includes('nav')) return 'header';
                if (t.includes('footer') || t.includes('legal')) return 'footer';
                return 'main';
              };

              type Group = { key: string; section: 'header' | 'main' | 'footer'; name: string; type: string; items: RecommendedComponent[] };
              const groups = new Map<string, Group>();

              for (const c of state.recommendedComponents) {
                const name = c.containerNames?.[0] || c.containerName || 'Main';
                const type = c.containerTypes?.[0] || getLegacyContainerType(c) || 'main';
                const section = sectionOf(type);
                const key = `${section}::${name}::${type}`;
                if (!groups.has(key)) {
                  groups.set(key, { key, section, name, type, items: [] });
                }
                groups.get(key)!.items.push(c);
              }

              const all = Array.from(groups.values());
              const headerGroups = all.filter((g) => g.section === 'header');
              const mainGroups = all.filter((g) => g.section === 'main');
              const footerGroups = all.filter((g) => g.section === 'footer');

              const renderComponentRow = (c: RecommendedComponent, idx: number) => {
                const compName = c.componentName || c.variantCode?.split('-')[0] || c.variantCode || 'компонент';
                const required = !!c.isRequired;
                const hasVariants = Array.isArray(c.availableVariants) && c.availableVariants.length > 1;

                const variantChoice = formData.componentVariantChoice[compName] || 'random';
                const optState = formData.optionalComponentsState[compName] || 'random';
                const isExcluded = !required && optState === 'exclude';

                const setVariant = (value: string) => {
                  setFormData((prev) => ({
                    ...prev,
                    componentVariantChoice: { ...prev.componentVariantChoice, [compName]: value }
                  }));
                };

                const setApply = (value: 'random' | 'include' | 'exclude') => {
                  setFormData((prev) => ({
                    ...prev,
                    optionalComponentsState: { ...prev.optionalComponentsState, [compName]: value }
                  }));
                };

                return (
                  <div key={`${compName}-${c.componentId}-${idx}`} className="u2-row u2-componentRow" style={{ alignItems: 'flex-start', gap: 12, opacity: isExcluded ? 0.55 : 1 }}>
                    <div style={{ minWidth: 0 }}>
                      <div className="u2-strong" style={{ fontSize: 13 }} data-tip={getComponentTip(compName)}>
                        {getComponentLabel(compName)}
                      </div>
                      <div className="u2-muted" style={{ fontSize: 12, marginTop: 4 }}>
                        {required ? 'Всегда на сайте' : 'Дополнительный блок'}
                        <div>{getComponentTip(compName)}</div>
                        {(c.containerNames?.length || 0) > 1 && <div>Области: {c.containerNames?.join(', ')}. Настройка действует на этот тип блока во всех областях.</div>}
                      </div>
                      <ComponentExample componentId={c.availableVariants?.find(v => v.variantCode === variantChoice)?.id || c.componentId} name={getComponentLabel(compName)} theme={formData.themeMode === 'template' ? currentTemplateTheme || 'corporate-blue' : formData.theme} automatic={hasVariants && variantChoice === 'random'} autoOpen={customizing && hasVariants && variantChoice !== 'random'} />
                    </div>

                    <div className="u2-compControls">
                      <div className="u2-miniField">
                        <div className="u2-miniLabel">Вариант</div>
                        <select
                          className="u2-select u2-miniSelect"
                          aria-label={`Вариант: ${getComponentLabel(compName)}`}
                          value={hasVariants ? variantChoice : 'fixed'}
                          disabled={!customizing || !hasVariants || isExcluded}
                          onChange={(e) => setVariant(e.target.value)}
                        >
                          {hasVariants ? (
                            <>
                              <option value="random">Автовыбор</option>
                              {c.availableVariants?.map((v) => (
                                <option key={v.id} value={v.variantCode}>
                                  {v.fullName || v.variantCode}
                                </option>
                              ))}
                            </>
                          ) : (
                            <option value="fixed">фиксированный</option>
                          )}
                        </select>
                      </div>

                      <div className="u2-miniField">
                        <div className="u2-miniLabel">Применение</div>
                        <select
                          className="u2-select u2-miniSelect"
                          aria-label={`Включение: ${getComponentLabel(compName)}`}
                          value={required ? 'required' : optState}
                          disabled={!customizing || required}
                          onChange={(e) => setApply(e.target.value as 'random' | 'include' | 'exclude')}
                        >
                          {required ? (
                            <option value="required">обязательный</option>
                          ) : (
                            <>
                              <option value="random">Автовыбор</option>
                              <option value="include">Включить</option>
                              <option value="exclude">Исключить</option>
                            </>
                          )}
                        </select>
                      </div>
                    </div>
                  </div>
                );
              };

              const renderGroup = (g: Group) => (
                <div key={g.key} className="u2-card u2-blockGroup" style={{ boxShadow: 'none', background: 'var(--u2-surface-1)' }}>
                  <div className="u2-cardInner">
                    <div className="u2-row" style={{ marginBottom: 10 }}>
                      <div className="u2-strong">{g.name}</div>
                      <span className="u2-badge">{g.items.length}</span>
                    </div>
                    <div className="u2-grid" style={{ gap: 10 }}>
                      {g.items.map(renderComponentRow)}
                    </div>
                  </div>
                </div>
              );

              const renderSection = (title: string, hint: string, list: Group[]) => (
                <div className="u2-card u2-blockSection" style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}>
                  <div className="u2-cardInner">
                    <div className="u2-row" style={{ marginBottom: 10 }}>
                      <div>
                        <div className="u2-strong">{title}</div>
                        <div className="u2-muted" style={{ fontSize: 12 }}>
                          {hint}
                        </div>
                      </div>
                      <span className="u2-badge">{list.length || '—'}</span>
                    </div>
                    <div className="u2-grid" style={{ gap: 10 }}>
                      {list.length > 0 ? list.map(renderGroup) : <div className="u2-muted" style={{ fontSize: 12 }}>Нет контейнеров.</div>}
                    </div>
                  </div>
                </div>
              );

              return (
                <div className="u2-card is-soft u2-blockStructure" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
                  <div className="u2-cardInner">
                    <div className="u2-row" style={{ marginBottom: 10 }}>
                      <div>
                        <div className="u2-strong">Структура и настройки компонентов</div>
                        <div className="u2-muted" style={{ fontSize: 12, marginTop: 6 }}>
                          В каждом блоке сразу виден список компонентов.
                        </div>
                      </div>
                      <span className="u2-badge">{state.recommendedComponents.length}</span>
                    </div>

                    <div className="u2-grid" style={{ gap: 14 }}>
                      {renderSection('Шапка', 'Контейнеры и компоненты шапки.', headerGroups)}
                      {renderSection('Основной блок', 'Основной контент (колонки отображаются только если они есть).', mainGroups)}
                      {renderSection('Футер', 'Футер и юридические блоки.', footerGroups)}
                    </div>
                  </div>
                </div>
              );
            })()}

          </div>
        )}

        {/* Шаг 5: Локализация (v5) */}
        {currentStep === 5 && (
          <div className="step-content">
            <div className="u2-cardHeader">
              <div>
                <h2 className="u2-title">Шаг {visibleSteps.indexOf(5) + 1}: Язык и регион</h2>
                <p className="u2-subtitle">{quickMode ? 'Выберите язык сайта и юрисдикцию.' : 'Выберите платформу, юрисдикцию и языки для сайта.'}</p>
              </div>
              <span className="u2-pill is-warn">обязательный</span>
            </div>

            <div className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
              <div className="u2-cardInner">
                <div className="u2-grid">
                  <div className="u2-field wp-platformField">
                    <div className="u2-labelRow">
                      <div className="u2-label wp-inlineLabel"><PlatformIcon platform={formData.platform} />Платформа</div>
                      <button type="button" aria-label="Пояснение"
                        className="u2-info"
                        tabIndex={0}
                        data-tip="Платформа влияет на стиль контента и правила compliance (какие формулировки/акценты допустимы)."
                      >
                        i
                      </button>
                    </div>
                    <div className="wp-platformChoices" role="group" aria-label="Рекламная платформа">
                      {(legalConfig.platforms.length ? legalConfig.platforms : [
                        { code: 'facebook', nameRu: 'Facebook / Meta Ads' }, { code: 'google', nameRu: 'Google Ads' },
                        { code: 'tiktok', nameRu: 'TikTok Ads' }, { code: 'universal', nameRu: 'Универсальный' }
                      ]).map(platform => <button key={platform.code} type="button"
                        className={`wp-platformChoice ${formData.platform === platform.code ? 'is-selected' : ''}`}
                        aria-pressed={formData.platform === platform.code}
                        onClick={() => handleFormChange('platform', platform.code)}>
                        <PlatformIcon platform={platform.code} /><span>{platform.nameRu}</span>
                        <span className="wp-choiceMark" aria-hidden="true">{formData.platform === platform.code ? '✓' : ''}</span>
                      </button>)}
                    </div>
                    <div className="u2-muted" style={{ fontSize: 12, marginTop: 6 }}>
                      Источник трафика/платформа.
                    </div>
                  </div>

                  <div className="u2-field">
                    <div className="u2-labelRow">
                      <div className="u2-label">Юрисдикция</div>
                      <button type="button" aria-label="Пояснение"
                        className="u2-info"
                        tabIndex={0}
                        data-tip="Юрисдикция определяет набор юридических документов и формулировки в футере."
                      >
                        i
                      </button>
                    </div>
                    <LocaleSelect label="Юрисдикция" kind="region" value={formData.geo} onChange={value => handleFormChange('geo', value)}
                      options={(legalConfig.jurisdictions.length ? legalConfig.jurisdictions : [
                        {code:'international',nameRu:'Международный / Весь мир'}, {code:'eu',nameRu:'Европейский союз (GDPR)'},
                        {code:'us',nameRu:'США'}, {code:'ru',nameRu:'Россия (152-ФЗ)'}
                      ]).map(item => ({value:item.code,label:item.nameRu}))} />
                    <div className="u2-muted" style={{ fontSize: 12, marginTop: 6 }}>
                      Влияет на требования и legal‑документы.
                    </div>
                  </div>

                  <div className="u2-field">
                    <div className="u2-labelRow">
                      <div className="u2-label">Язык контента</div>
                      <button type="button" aria-label="Пояснение" className="u2-info" tabIndex={0} data-tip="Язык основного текста сайта (заголовки, описания, CTA).">
                        i
                      </button>
                    </div>
                    <LocaleSelect label="Язык сайта" kind="language" value={formData.contentLanguage} onChange={value => handleFormChange('contentLanguage', value)}
                      options={(availableLanguages.length ? availableLanguages : [
                        {code:'en',nativeName:'English',name:'Английский',direction:'ltr'}, {code:'ru',nativeName:'Русский',name:'Русский',direction:'ltr'}
                      ]).map(item => ({value:item.code,label:`${item.nativeName}${item.direction === 'rtl' ? ' (справа налево)' : ''}`,search:item.name}))} />
                    <div className="u2-muted" style={{ fontSize: 12, marginTop: 6 }}>
                      Язык текстов сайта. Флаг служит подсказкой; юрисдикция выбирается отдельно.
                    </div>
                  </div>

                  <div className="u2-field wp-legalLanguageField">
                    <div className="u2-labelRow">
                      <div className="u2-label">Язык юридических документов</div>
                      <button type="button" aria-label="Пояснение"
                        className="u2-info"
                        tabIndex={0}
                        data-tip="Если не выбран — используем язык контента. Может отличаться, если документы должны быть на другом языке."
                      >
                        i
                      </button>
                    </div>
                    <LocaleSelect label="Язык документов" kind="language" value={formData.legalDocsLanguage} onChange={value => handleFormChange('legalDocsLanguage', value)}
                      options={[{value:'',label:'Как у сайта'}, {value:'en',label:'Английский'}]} />
                    <div className="u2-muted" style={{ fontSize: 12, marginTop: 6 }}>
                      Язык документов в футере. Перед публикацией проверьте содержание и реквизиты с юристом.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Шаг 6: Фон сайта (v5) */}
        {currentStep === 6 && (
          <div className="step-content">
            <div className="u2-cardHeader">
              <div>
                <h2 className="u2-title">Шаг {visibleSteps.indexOf(6) + 1}: Фон сайта</h2>
                <p className="u2-subtitle">
                  Глобальный фон — это фон <span className="u2-strong">под элементами</span> (под блоками контента). Он не меняет стили компонентов, а просто добавляет общий фон на всех страницах.
                </p>
              </div>
              <span className="u2-pill">визуал</span>
            </div>

            <div className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent', marginBottom: 14 }}>
              <div className="u2-cardInner">
                <label className="u2-row" style={{ justifyContent: 'flex-start', gap: 10, cursor: 'pointer', alignItems: 'flex-start' }}>
                  <input
                    type="checkbox"
                    checked={formData.backgroundEnabled}
                    onChange={(e) => handleFormChange('backgroundEnabled', e.target.checked)}
                  />
                  <span>
                    <span className="u2-strong">Использовать глобальный фон</span>{' '}
                    <button type="button" aria-label="Пояснение"
                      className="u2-info"
                      tabIndex={0}
                      data-tip="Глобальный фон — это фон под контентом (под блоками). Он не меняет оформление карточек/кнопок, а только добавляет общий фон страницы."
                    >
                      i
                    </button>
                    <br />
                    <span className="u2-muted" style={{ fontSize: 12 }}>
                      Добавляет фон под блоками/элементами на всех страницах (не меняет стили компонентов).
                    </span>
                  </span>
                </label>

                <div className="u2-divider" style={{ margin: '10px 0' }} />

                <div>
                  <div className="u2-labelRow">
                    <div className="u2-label">Тип фона</div>
                    <button type="button" aria-label="Пояснение"
                      className="u2-info"
                      tabIndex={0}
                      data-tip="Статический: картинка/подборка (легче). Динамический: эффект/пресет (может быть тяжелее, но выглядит “дороже”)."
                    >
                      i
                    </button>
                  </div>
                  <div className="u2-modeGrid wp-backgroundModes" style={{ marginTop: 10 }}>
                    <div
                      className={`u2-modeCard ${formData.backgroundMode === 'random' ? 'is-selected' : ''}`}
                      onClick={() => handleFormChange('backgroundMode', 'random')}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleFormChange('backgroundMode', 'random');
                        }
                      }}
                    >
                      <div className="u2-modeTitle">Случайный</div>
                      <div className="u2-modeHint">Автоматический выбор из всех доступных фонов.</div>
                    </div>
                    <div
                      className={`u2-modeCard ${formData.backgroundMode === 'static' ? 'is-selected' : ''}`}
                      onClick={() => handleFormChange('backgroundMode', 'static')}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleFormChange('backgroundMode', 'static');
                        }
                      }}
                    >
                      <div className="u2-modeTitle">Статический</div>
                      <div className="u2-modeHint">Неподвижные изображения и цветовые переходы.</div>
                    </div>
                    <div
                      className={`u2-modeCard ${formData.backgroundMode === 'dynamic' ? 'is-selected' : ''}`}
                      onClick={() => handleFormChange('backgroundMode', 'dynamic')}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleFormChange('backgroundMode', 'dynamic');
                        }
                      }}
                    >
                      <div className="u2-modeTitle">Динамический</div>
                      <div className="u2-modeHint">Анимированные эффекты с движением на странице.</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* #VisualModernization: эффект фонов секционных компонентов.
                Отдельно от глобального background — задаёт мягкие градиенты/glass/mesh/glow
                поверх секций hero/cta/pricing/faq/features/blog. */}
            <div className="u2-card is-soft wp-backgroundDetails" style={{ boxShadow: 'none', borderColor: 'transparent', marginTop: 14 }}>
              <div className="u2-cardInner">
                <div className="u2-row" style={{ marginBottom: 10 }}>
                  <div>
                    <div className="u2-strong">Эффект секционных компонентов</div>
                    <div className="u2-muted" style={{ fontSize: 12 }}>
                      Единый аккуратный эффект фонов секций (hero, cta, pricing и т.д.). Не конфликтует с глобальным фоном сайта.
                    </div>
                  </div>
                </div>
                <div className="u2-row" style={{ gap: 16, alignItems: 'center' }}>
                  <select
                    className="u2-select"
                    value={formData.componentVisualEffect}
                    onChange={(e) => handleFormChange('componentVisualEffect', e.target.value as typeof formData.componentVisualEffect)}
                    style={{ flex: 1, maxWidth: 320 }}
                  >
                    <option value="none">Без эффекта</option>
                    <option value="soft-gradient">Мягкий градиент</option>
                    <option value="glass-lite">Матовое стекло</option>
                    <option value="mesh-light">Многоцветный фон</option>
                    <option value="glow-accent">Акцентное свечение</option>
                  </select>
                </div>
                <div className="u2-muted" style={{ fontSize: 11, marginTop: 8 }}>
                  Эффект применяется единообразно ко всем поддерживающим его компонентам сборки и автоматически адаптируется к выбранной теме.
                </div>
              </div>
            </div>

            {/* Автоматическая плотность подложек сохраняет читаемость поверх фона. */}
            {formData.backgroundEnabled && (
              <div className="u2-card is-soft wp-backgroundDetails" style={{ boxShadow: 'none', borderColor: 'transparent', marginTop: 14 }}>
                <div className="u2-cardInner">
                  <div className="u2-row" style={{ marginBottom: 10 }}>
                    <div>
                      <div className="u2-strong">Плотность фона блоков</div>
                      <div className="u2-muted" style={{ fontSize: 12 }}>
                        Подобрана автоматически: фон остаётся виден, а текст — читаемым.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <section className="wp-backgroundUpload" aria-label="Загрузить свой фон">
              <label className="u2-btn is-success">{backgroundUploading ? 'Загружаем…' : 'Загрузить свой фон'}<input type="file" accept="image/png,image/jpeg,.png,.jpg,.jpeg" aria-label="Изображение для фона" disabled={backgroundUploading} onChange={event => { void uploadOwnBackground(event.target.files?.[0]); event.target.value = ''; }} /></label>
              <p className="u2-muted">PNG или JPEG, до 8 МБ. Минимум 800 × 600, максимум 6000 × 6000 пикселей. Изображение войдёт в готовый ZIP.</p>
              {backgroundUploadError && <p className="error-message" role="alert">{backgroundUploadError}</p>}
            </section>
            {(() => {
              const isRandom = formData.backgroundMode === 'random';
              const isDynamic = formData.backgroundMode === 'dynamic';
              
              // При режиме "случайный" не показываем селектор фона
              if (isRandom) {
                return (
                  <div className="u2-card is-soft" style={{ marginTop: 16, padding: 20, textAlign: 'center' }}>
                    <div className="u2-strong">Случайный фон</div>
                    <div className="u2-muted" style={{ marginTop: 8, fontSize: 13 }}>
                      При генерации будет автоматически выбран случайный фон из доступных статических и динамических вариантов.
                    </div>
                  </div>
                );
              }
              
              const selectedPreset = isDynamic
                ? bgPresets.find((p) => p.id === formData.backgroundPresetId) || null
                : null;
              const selectedCurated = !isDynamic
                ? bgCurated.find((bg) => bg.id === formData.backgroundCuratedId) || null
                : null;
              const isUploaded = !isDynamic && Boolean(formData.backgroundUploadId);
              const hasSelectedBackground = isDynamic ? Boolean(selectedPreset) : isUploaded || Boolean(selectedCurated);
              const selectedName = isDynamic
                ? selectedPreset
                  ? String(selectedPreset.name || '—')
                  : 'Подбор при генерации'
                : isUploaded ? uploadedBackgroundName : selectedCurated
                  ? String(selectedCurated.name || '—')
                  : 'Подбор при генерации';
              const selectedMeta = isDynamic ? 'Анимированный эффект' : 'Неподвижный фон';
              const selectedThumbUrl = isDynamic
                ? selectedPreset
                  ? selectedPreset.preview_url || null
                  : null
                : isUploaded ? uploadedPreviewUrl : selectedCurated
                  ? selectedCurated.url || null
                  : null;
              const canOpenPreview = isDynamic ? Boolean(selectedPreset) : Boolean(selectedThumbUrl);
              const searchTerm = backgroundSearch.trim().toLocaleLowerCase('ru-RU');
              const visiblePresets = bgPresets.filter((preset) =>
                `${preset.name} ${preset.subtype || ''}`.toLocaleLowerCase('ru-RU').includes(searchTerm)
              );
              const visibleCurated = bgCurated.filter((bg) =>
                `${bg.name} ${bg.type || ''} ${bg.color_scheme || ''}`.toLocaleLowerCase('ru-RU').includes(searchTerm)
              );

              return (
                <div className="u2-grid cols-2 u2-bgChooser" style={{ alignItems: 'start' }}>
                  <div className="u2-card is-soft u2-bgGallery" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
                    <div className="u2-cardInner">
                      <div className="u2-row" style={{ marginBottom: 10 }}>
                        <div>
                          <div className="u2-strong">Галерея фонов</div>
                          <div className="u2-muted" style={{ fontSize: 12 }}>
                            Выберите вариант. Двойной клик открывает крупное превью.
                          </div>
                        </div>
                        <span className="u2-badge">{isDynamic ? 'Анимированные' : 'Неподвижные'}</span>
                      </div>

                      {formData.backgroundEnabled ? (
                        <input
                          className="u2-input u2-bgSearch"
                          type="search"
                          value={backgroundSearch}
                          onChange={(event) => setBackgroundSearch(event.target.value)}
                          placeholder="Поиск фона по названию"
                          aria-label="Поиск фона по названию"
                        />
                      ) : null}
                      {formData.backgroundEnabled && (selectedPreset || selectedCurated) ? (
                        <button
                          className="u2-btn is-ghost u2-bgShowSelected"
                          type="button"
                          onClick={() => selectedBackgroundRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                        >
                          Показать выбранный фон ↑
                        </button>
                      ) : null}

                      {!formData.backgroundEnabled ? (
                        <div className="u2-muted" style={{ fontSize: 12 }}>
                          Глобальный фон выключен.
                        </div>
                      ) : (
                        <div className="u2-grid cols-3 u2-bgTiles">
                          {isDynamic
                            ? visiblePresets.map((preset) => {
                              const id = preset.id;
                              const isSelected = formData.backgroundPresetId === id;
                              const url = preset.preview_url;
                              const hasThumb = Boolean(url);

                              return (
                                <div
                                  key={`p-${id}`}
                                  className={`u2-bgTile ${isSelected ? 'is-selected' : ''}`}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => {
                                    if (!formData.backgroundEnabled) return;
                                    handleFormChange('backgroundPresetId', id);
                                    handleFormChange('backgroundCuratedId', null);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      if (!formData.backgroundEnabled) return;
                                      handleFormChange('backgroundPresetId', id);
                                      handleFormChange('backgroundCuratedId', null);
                                    }
                                  }}
                                  onDoubleClick={() => {
                                    if (!formData.backgroundEnabled) return;
                                    handleFormChange('backgroundPresetId', id);
                                    handleFormChange('backgroundCuratedId', null);
                                    openDynamicPreviewModal(`Превью фона: ${preset.name}`, id);
                                  }}
                                >
                                  <div
                                    className={`u2-bgTileThumb ${hasThumb || preset.preview_html ? '' : 'is-empty'}`}
                                    style={{ background: previewColorScheme === 'dark' ? '#111827' : '#f8fafc' }}
                                  >
                                    {preset.preview_html ? <iframe title={`Фон ${preset.name}`} srcDoc={backgroundPreviewDocument(preset.preview_html, previewColorScheme)} sandbox="allow-scripts" loading="lazy" tabIndex={-1} className="wp-backgroundThumbnail" /> : <span className="u2-bgTileThumbLabel">{preset.name}</span>}
                                  </div>
                                  <div className="u2-bgTileMeta">
                                    <span title={String(preset.name || '')}>{String(preset.name || '—')}</span>
                                  </div>
                                </div>
                              );
                            })
                            : visibleCurated.map((bg) => {
                              const id = bg.id;
                              const isSelected = formData.backgroundCuratedId === id;
                              const url = bg.url;
                              const hasThumb = Boolean(url);

                              return (
                                <div
                                  key={`c-${id}`}
                                  className={`u2-bgTile ${isSelected ? 'is-selected' : ''}`}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => {
                                    if (!formData.backgroundEnabled) return;
                                    handleFormChange('backgroundCuratedId', id);
                                    handleFormChange('backgroundPresetId', null);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      if (!formData.backgroundEnabled) return;
                                      handleFormChange('backgroundCuratedId', id);
                                      handleFormChange('backgroundPresetId', null);
                                    }
                                  }}
                                  onDoubleClick={() => {
                                    if (!url) return;
                                    openImagePreviewModal(`Превью фона: ${bg.name}`, url);
                                  }}
                                >
                                  <div
                                    className={`u2-bgTileThumb ${hasThumb ? '' : 'is-empty'}`}
                                    style={hasThumb ? { backgroundImage: backgroundPreviewImage(url) } : undefined}
                                  >
                                    {!hasThumb ? <span className="u2-bgTileThumbLabel">Нет превью</span> : null}
                                  </div>
                                  <div className="u2-bgTileMeta">
                                    <span title={String(bg.name || '')}>{String(bg.name || '—')}</span>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )}
                      {formData.backgroundEnabled && (isDynamic ? visiblePresets : visibleCurated).length === 0 ? (
                        <div className="u2-muted" style={{ marginTop: 12 }}>По этому запросу фоны не найдены.</div>
                      ) : null}
                    </div>
                  </div>

                  <div ref={selectedBackgroundRef} className="u2-card is-soft u2-bgSelected" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
                    <div className="u2-cardInner">
                      <div className="u2-row" style={{ marginBottom: 10 }}>
                        <div>
                          <div className="u2-strong">{hasSelectedBackground ? 'Выбранный фон' : 'Фон для сайта'}</div>
                          <div className="u2-muted" style={{ fontSize: 12 }}>
                            {selectedName} · {selectedMeta}
                          </div>
                        </div>
                        <div className="u2-toolbarGroup">
                          <button
                            type="button"
                            className="u2-btn is-secondary"
                            disabled={!canOpenPreview}
                            onClick={() => {
                              if (!canOpenPreview) return;
                              if (isDynamic && selectedPreset) {
                                openDynamicPreviewModal(`Превью фона: ${selectedName}`, selectedPreset.id);
                                return;
                              }
                              if (selectedThumbUrl) {
                                openImagePreviewModal(`Превью фона: ${selectedName}`, selectedThumbUrl);
                              }
                            }}
                          >
                            Превью
                          </button>
                          <button
                            type="button"
                            className="u2-btn is-ghost"
                            onClick={() => {
                              handleFormChange('backgroundPresetId', null);
                              handleFormChange('backgroundCuratedId', null);
                              handleFormChange('backgroundUploadId', '');
                            }}
                            disabled={!formData.backgroundEnabled || !hasSelectedBackground}
                          >
                            Сбросить
                          </button>
                        </div>
                      </div>

                      {isDynamic && selectedPreset ? (
                        <div className="u2-muted" style={{ fontSize: 12, marginBottom: 8 }}>
                          Анимация показана на {previewColorScheme === 'dark' ? 'тёмной' : 'светлой'} подложке выбранной темы.
                        </div>
                      ) : null}
                      <div
                        className="u2-previewFrame"
                        style={
                          isDynamic
                            ? { height: 320, borderStyle: 'solid', padding: 0, position: 'relative', overflow: 'hidden', display: 'block' }
                            : selectedThumbUrl
                              ? { height: 320, borderStyle: 'solid', backgroundImage: backgroundPreviewImage(selectedThumbUrl), backgroundPosition: 'center', backgroundSize: 'cover' }
                              : { height: 320 }
                        }
                      >
                        {isDynamic ? (
                          <>
                            {dynamicPreview.loading ? (
                              <div className="u2-muted" style={{ fontSize: 12 }}>
                                Загрузка превью…
                              </div>
                            ) : null}
                            {!dynamicPreview.loading && dynamicPreview.error ? (
                              <div className="u2-muted" style={{ fontSize: 12 }}>
                                {dynamicPreview.error}
                              </div>
                            ) : null}
                            {!dynamicPreview.loading && !dynamicPreview.error && dynamicPreviewSrcDoc ? (
                              <iframe
                                title={`Превью динамического фона ${selectedName}`}
                                sandbox="allow-scripts"
                                srcDoc={dynamicPreviewSrcDoc}
                                style={{ width: '100%', height: '100%', border: 0 }}
                              />
                            ) : null}
                            {!dynamicPreview.loading && !dynamicPreview.error && !dynamicPreviewSrcDoc ? (
                              <div className="u2-bgEmptyPreview">Выберите фон в галерее, чтобы увидеть его здесь. Иначе фон подберётся при генерации.</div>
                            ) : null}
                          </>
                        ) : (
                          !selectedThumbUrl ? <div className="u2-bgEmptyPreview">Выберите фон в галерее, чтобы увидеть его здесь. Иначе фон подберётся при генерации.</div> : ''
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Шаг 7: Генерация */}
        {currentStep === 7 && (
          <div className="step-content">
            <div className="u2-cardHeader">
              <div>
                <h2 className="u2-title">Шаг {visibleSteps.indexOf(7) + 1}: Генерация</h2>
                <p className="u2-subtitle">
                  Проверьте конфигурацию и запустите создание сайта. Готовый сайт можно будет открыть в браузере и скачать ZIP-архивом.
                </p>
              </div>
            </div>

            {state.generationResult && (
              <div ref={generationResultRef} tabIndex={-1} className="wp-generationResult" role="region" aria-label="Готовый сайт">
                <div className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
                  <div className="u2-cardInner">
                    <div className="u2-row" style={{ marginBottom: 10 }}>
                      <div>
                        <h3 className="wp-resultTitle">Сайт готов — скачайте архив</h3><p className="u2-muted">ZIP содержит страницы и ресурсы сайта. Результат также сохранён в истории.</p>
                        <div className="u2-muted" style={{ fontSize: 12, marginTop: 6 }}>
                          Время генерации: {Math.round(state.generationResult.generationTime / 1000)} сек
                        </div>
                      </div>
                      <span className="u2-pill is-ok">готово</span>
                    </div>

                    <div className="u2-toolbarGroup" style={{ justifyContent: 'flex-start', flexWrap: 'wrap' }}>
                      {state.generationResult.downloadUrl ? (
                        <a href={addTokenToUrl(state.generationResult.downloadUrl)} className="u2-btn is-primary is-lg wp-downloadSite" download>
                          Скачать сайт · ZIP
                        </a>
                      ) : null}

                      {state.generationResult.previewUrl ? (
                        <>
                          <a
                            href={buildPreviewUrl(state.generationResult.previewUrl)}
                            onClick={(event) => {
                              event.preventDefault();
                              void openPreviewInNewTab(state.generationResult?.previewUrl);
                            }}
                            className="u2-btn is-secondary"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Открыть превью
                          </a>
                          <button
                            type="button"
                            className="u2-btn is-ghost"
                            onClick={() => void openPreviewInSameTab(state.generationResult?.previewUrl)}
                          >
                            Превью в этой вкладке
                          </button>
                          <PublicPreviewLink generationId={state.generationResult!.previewUrl!.match(/preview-v2\/([^/]+)/)?.[1] || String(state.generationResult!.id)} />
                        </>
                      ) : null}

                      <button onClick={() => navigate('/history')} className="u2-btn is-ghost" type="button">
                        История генераций
                      </button>
                    </div>
                  </div>
                </div>

                {renderAvailabilitySummary(state.generationResult?.availability || state.preview?.availability || null)}
                {renderWarningsPanel(
                  state.generationResult?.warnings && state.generationResult.warnings.length > 0
                    ? state.generationResult.warnings
                    : state.previewWarnings,
                  'Предупреждения генерации'
                )}
              </div>
            )}
                {/* 🆕 #F-115: Прогресс-бар генерации с компонентами */}
                {shouldShowProgressPanel && quickMode && <section className="wp-quickProgress" aria-label="Генерация сайта">
                  <h3>Создаём ваш сайт</h3>
                  <p className="u2-muted">Это займёт несколько минут. Результат появится здесь и в истории.</p>
                  <progress max={100} value={generationProgress.percentage} aria-label="Прогресс генерации" />
                  <span role="status">{generationProgress.status === 'queued' ? 'Ожидает своей очереди' : `${generationProgress.percentage}%`}</span>
                </section>}
                {shouldShowProgressPanel && !quickMode && (
                  <div className={`generation-progress ${state.generationInProgress ? '' : 'generation-progress--static'}`}>
                    <div className="progress-header">
                      <h3>
                        {state.generationInProgress
                          ? 'Генерация сайта'
                          : 'Отладка компонентов (последняя сборка)'}
                      </h3>
                      {state.generationInProgress && (
                      <span className="progress-percentage">{generationProgress.percentage}%</span>
                      )}
                    </div>

                    {/* 🆕 Таймер и советы */}
                    {state.generationInProgress && (
                      <div className="generation-timer-section">
                        <div className="generation-timer">
                          <ProgressIcon status="processing" />
                          <span className="timer-value">
                            {Math.floor(elapsedSeconds / 60)}:{String(elapsedSeconds % 60).padStart(2, '0')}
                          </span>
                          <span className="timer-estimate">
                            Генерация обычно занимает 2–6 минут; время зависит от состава сайта.
                          </span>
                        </div>
                        <div className="generation-tip">
                          {generationTips[currentTipIndex]}
                        </div>
                      </div>
                    )}

                    {/* Общий прогресс-бар */}
                    {state.generationInProgress && (
                    <div className="progress-bar-container">
                      <div
                        className={`progress-bar-fill ${generationProgress.status === 'failed' ? 'error' : ''}`}
                        style={{ width: `${generationProgress.percentage}%` }}
                      ></div>
                    </div>
                    )}

                    {/* Текущий этап */}
                    {generationProgress.stepName && (
                      <div className="progress-current-step">
                        <span className="current-step-icon" aria-hidden="true">
                          <ProgressIcon status={generationProgress.status} />
                        </span>
                        <span className="current-step-text">{generationProgress.stepName}</span>
                      </div>
                    )}

                    {!state.generationInProgress && debugModeActive && (
                      <p className="debug-mode-hint">
                        Список компонентов зафиксирован после последней генерации и будет доступен,
                        пока не запущена новая сборка или не отключён режим отладки.
                      </p>
                    )}

                    {/* Список компонентов с их статусами */}
                    {orderedComponents.length > 0 && (
                      <details className="components-progress-list wp-progressDetails">
                        <summary className="components-list-title">
                          Компоненты ({orderedComponents.filter(c => c.status === 'completed').length}/{orderedComponents.length})
                        </summary>
                        <div className="components-grid">
                          {orderedComponents.map((component) => {
                            const variantInfo = component.variantCode ? ` (${component.variantCode})` : '';

                              return (
                                <div
                                  key={component.id || component.name}
                                  className={`component-item component-item--${component.status}`}
                                >
                                <div className="component-order">#{component.order}</div>
                                  <div className="component-status-icon">
                                    <ProgressIcon status={component.status} />
                                  </div>
                                  <div className="component-info">
                                  <span className="component-name">{component.name}{variantInfo}</span>
                                  {component.containerName && (
                                    <span className="component-meta">{component.containerName}</span>
                                  )}
                                    <span className="component-status-text">
                                      {component.status === 'completed' && 'Готов'}
                                      {component.status === 'processing' && 'Генерируется...'}
                                      {component.status === 'failed' && 'Ошибка'}
                                      {component.status === 'pending' && 'Ожидание'}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </details>
                    )}

                    {debugModeActive && orderedComponents.length > 0 && (
                      <div className="components-detailed-list">
                        <h4 className="components-list-title">Точные компоненты</h4>
                        <div className="components-detailed-grid">
                          {orderedComponents.map(component => {
                            // Формируем полное техназвание: name-variantCode (например header-mega)
                            const fullTechName = component.variantCode && component.variantCode !== component.name
                              ? `${component.name}-${component.variantCode}`
                              : component.name;

                            return (
                            <div
                              key={`detailed-${component.id || component.order}`}
                              className="component-detailed-item"
                            >
                              <div className="component-detailed-order">#{component.order}</div>
                              <div className="component-detailed-info">
                                <span className="component-tech-name">
                                  {fullTechName}
                                </span>
                                <span className="component-meta-line">
                                  {component.containerName
                                    ? `Контейнер: ${component.containerName}`
                                    : 'Контейнер: —'}
                                </span>
                                <span className="component-meta-line">
                                  ID: {component.componentId ?? '—'}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}

                    {/* Текущий компонент (если генерируется) */}
                    {generationProgress.currentComponent && (
                      <div className="current-component-indicator">
                        <span className="indicator-text">
                          Генерируется: {generationProgress.currentComponent.name}
                          {generationProgress.currentComponent.variantCode ? ` (${generationProgress.currentComponent.variantCode})` : ''}
                          {generationProgress.currentComponent.container ? ` · ${generationProgress.currentComponent.container}` : ''}
                          {` (${generationProgress.currentComponent.index}/${generationProgress.currentComponent.total})`}
                        </span>
                      </div>
                    )}

                    {/* Ошибка */}
                    {generationProgress.error && (
                      <div className="progress-error">
                        <span className="error-icon" aria-hidden="true">!</span>
                        <span className="error-text">{generationProgress.error}</span>
                      </div>
                    )}
                  </div>
                )}
            <div className="wp-generationWorkspace">
              {/* Read-only summary */}
              <div className="u2-card is-soft wp-buildSummary" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
                <div className="u2-cardInner">
                  <div className="u2-row" style={{ marginBottom: 10 }}>
                    <div>
                      <div className="u2-strong">Ваш сайт</div>
                      <div className="u2-muted" style={{ fontSize: 12 }}>
                        Нажмите «Изменить» у нужной настройки. Остальные выбранные значения сохранятся.
                      </div>
                    </div>
                    <span className="u2-badge">{formData.siteType === 'multipage' ? 'Несколько страниц' : 'Одна страница'}</span>
                  </div>
                  <div className="u2-grid cols-4 u2-reviewSummary">
                    <div className="u2-card" style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}>
                      <div className="u2-cardInner">
                        <div className="u2-row"><div className="u2-kpiLabel">Шаблон</div><button className="u2-btn is-ghost" type="button" disabled={busy} onClick={() => handleGoToCompletedStep(1)} aria-label="Изменить: Шаблон">Изменить</button></div>
                        <div className="u2-strong">{selectedTemplate ? selectedTemplate.name : '—'}</div>
                      </div>
                    </div>
                    <div className="u2-card" style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}>
                      <div className="u2-cardInner">
                        <div className="u2-row"><div className="u2-kpiLabel">Тема</div><button className="u2-btn is-ghost" type="button" disabled={busy} onClick={() => handleGoToCompletedStep(2)} aria-label="Изменить: Тема">Изменить</button></div>
                        <div className="u2-strong">{formData.themeMode === 'template' ? 'По правилам шаблона' : getThemeName(formData.theme)}</div>
                      </div>
                    </div>
                    <div className="u2-card" style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}>
                      <div className="u2-cardInner">
                        <div className="u2-row"><div className="u2-kpiLabel">Контент</div><button className="u2-btn is-ghost" type="button" disabled={busy} onClick={() => handleGoToCompletedStep(3)} aria-label="Изменить: Контент">Изменить</button></div>
                        <div className="u2-strong">{selectedContentTheme ? selectedContentTheme.label : '—'}</div>{formData.companyName && <p>{formData.companyName}</p>}{formData.siteDescriptionOverride && <p className="u2-muted">{formData.siteDescriptionOverride}</p>}
                      </div>
                    </div>
                    <div className="u2-card" style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}>
                      <div className="u2-cardInner">
                        <div className="u2-row"><div className="u2-kpiLabel">Фон</div><button className="u2-btn is-ghost" type="button" disabled={busy} onClick={() => handleGoToCompletedStep(6)} aria-label="Изменить: Фон">Изменить</button></div>
                        <div className="u2-strong">
                          {(() => {
                            if (!formData.backgroundEnabled) return 'выключен';
                            if (formData.backgroundMode === 'dynamic') {
                              const p = bgPresets.find((x) => x.id === formData.backgroundPresetId);
                              return p ? String(p.name) : 'авто';
                            }
                            const bg = bgCurated.find((x) => x.id === formData.backgroundCuratedId);
                            return bg ? String(bg.name) : 'авто';
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

                <div className="u2-card wp-launchCard">
                  <div className="u2-cardInner">
                    <div className="u2-cardHeader">
                      <div>
                        <h3 className="u2-title">{state.generationResult ? 'Новая сборка' : 'Запуск сборки'}</h3>
                        <p className="u2-subtitle">
                          {quickMode ? 'Всё готово. Создадим тексты, подберём изображения и соберём сайт.' : 'Сначала проверяем настройки. Создание текстов, подбор изображений и сборка начинаются только после запуска.'}
                        </p>
                      </div>
                      <div className="u2-toolbarGroup">
                        {(!quickMode || (!previewIsCurrent && Boolean(state.error))) && <button
                          type="button"
                          className="u2-btn is-secondary"
                          onClick={() => void generatePreview()}
                          disabled={!componentsReady || Boolean(contentError) || state.previewLoading || busy}
                        >
                          Обновить проверку
                        </button>}
                        <button
                          type="button"
                          className="u2-btn is-success is-lg"
                          onClick={startGeneration}
                          disabled={busy || batchInProgress || !previewIsCurrent || !componentsReady || Boolean(contentError) || !canAffordGeneration}
                        >
                          {busy ? 'Создаём сайт…' : state.generationResult ? 'Создать ещё один сайт' : 'Создать сайт'}
                        </button>
                      </div>
                    </div>

                    <div className="u2-launchDetails" role="status">
                      {state.previewLoading ? 'Проверяем конфигурацию…' : previewIsCurrent ? (canAffordGeneration ? 'Конфигурация проверена. Можно запускать.' : 'Конфигурация проверена. Для запуска нужен доступный лимит или достаточный баланс.') : 'Для запуска нужна успешная проверка конфигурации.'}
                      {pricing && <p>Стоимость одной генерации: <strong>{formatBalanceUSD(formData.siteType === 'multipage' ? pricing.multi_page : pricing.single_page)}</strong>. {freeRemaining > 0 ? `Эта генерация бесплатная — осталось бесплатных: ${freeRemaining}.` : account && account.generation_limit > 0 ? `Сначала используется доступная генерация из лимита (осталось: ${account.generation_limit}).` : 'Оплата с общего баланса портала.'}</p>}
                      {!pricing && <p>Не удалось получить стоимость. Уточните тариф перед запуском.</p>}
                      {!account && <p>Не удалось получить баланс и лимит. Обновите страницу перед запуском — настройки сохранены.</p>}
                      {account && <p>Ваш баланс: {formatBalanceUSD(account.balance)}. <a href={`/api/portal/balance/topup?source=whitepage-generator&returnUrl=${encodeURIComponent(window.location.href)}`}>Пополнить баланс</a></p>}
                      <p>Генерация может занять несколько минут. Готовые сайты доступны в истории.</p>
                    </div>

                  </div>
                </div>


              {/* Editable localization */}
              <div className="u2-card is-soft wp-reviewLocalization" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
                <div className="u2-cardInner">
                  <div className="u2-row" style={{ marginBottom: 10 }}>
                    <div>
                      <div className="u2-strong">Язык и регион</div>
                      <div className="u2-muted" style={{ fontSize: 12 }}>
                        Эти настройки определяют язык сайта и набор документов.
                      </div>
                    </div>
                  </div>

                  <div className="u2-grid cols-4">
                    <div className="u2-field">
                      <div className="u2-label wp-inlineLabel"><PlatformIcon platform={formData.platform} />Платформа</div>
                      <select
                        className="u2-select"
                        aria-label="Рекламная платформа"
                            value={formData.platform}
                        onChange={(e) => handleFormChange('platform', e.target.value)}
                      >
                        {legalConfig.platforms.length > 0 ? (
                          legalConfig.platforms.map((p) => (
                            <option key={p.code} value={p.code}>
                              {p.nameRu}
                            </option>
                          ))
                        ) : (
                          <>
                            <option value="facebook">Facebook / Meta Ads</option>
                            <option value="google">Google Ads</option>
                            <option value="tiktok">TikTok Ads</option>
                            <option value="universal">Универсальный</option>
                          </>
                        )}
                      </select>
                    </div>

                    <div className="u2-field">
                      <div className="u2-label">Юрисдикция</div>
                      <LocaleSelect label="Юрисдикция" kind="region" value={formData.geo} onChange={value => handleFormChange('geo', value)}
                      options={(legalConfig.jurisdictions.length ? legalConfig.jurisdictions : [
                        {code:'international',nameRu:'Международный / Весь мир'}, {code:'eu',nameRu:'Европейский союз (GDPR)'},
                        {code:'us',nameRu:'США'}, {code:'ru',nameRu:'Россия (152-ФЗ)'}
                      ]).map(item => ({value:item.code,label:item.nameRu}))} />
                    </div>

                    <div className="u2-field">
                      <div className="u2-label">Язык</div>
                      <LocaleSelect label="Язык сайта" kind="language" value={formData.contentLanguage} onChange={value => handleFormChange('contentLanguage', value)}
                      options={(availableLanguages.length ? availableLanguages : [
                        {code:'en',nativeName:'English',name:'Английский',direction:'ltr'}, {code:'ru',nativeName:'Русский',name:'Русский',direction:'ltr'}
                      ]).map(item => ({value:item.code,label:`${item.nativeName}${item.direction === 'rtl' ? ' (справа налево)' : ''}`,search:item.name}))} />
                    </div>

                    <div className="u2-field">
                      <div className="u2-label">Язык документов</div>
                      <LocaleSelect label="Язык документов" kind="language" value={formData.legalDocsLanguage} onChange={value => handleFormChange('legalDocsLanguage', value)}
                      options={[{value:'',label:'Как у сайта'}, {value:'en',label:'Английский'}]} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ height: 14 }} />

            {state.previewLoading && (
              <div className="preview-loading">
                <div className="spinner"></div>
                <p>Загрузка параметров...</p>
              </div>
            )}

            {(
              <details className="wp-buildDetails"><summary>Состав сайта, документы и дополнительные режимы</summary>
              <div className="u2-grid" style={{ gap: 14 }}>
                {isAdmin && renderTemplateAccessSummary(state.templateAccess || state.preview?.templateAccess)}
                {isAdmin && renderAvailabilitySummary(state.preview?.availability)}
                {renderWarningsPanel(state.preview?.warnings || state.previewWarnings, 'Предупреждения по шаблону')}

                <div className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
                  <div className="u2-cardInner">
                    <div className="u2-row" style={{ marginBottom: 10 }}>
                      <div>
                        <div className="u2-row"><div className="u2-strong">Состав сайта</div><button type="button" className="u2-btn is-ghost" disabled={busy} onClick={() => handleGoToCompletedStep(4)}>Изменить блоки</button></div>
                        <div className="u2-muted" style={{ fontSize: 12 }}>
                          Это правила включения блоков. При автовыборе точный состав определяется во время генерации.
                        </div>
                      </div>
                      <span className="u2-badge">{finalComponentsForReview.length || '—'}</span>
                    </div>
                    <div className="u2-compChips">
                      {finalComponentsForReview.length > 0 ? (
                        finalComponentsForReview.map((c) => (
                          <span
                            key={c.key}
                            className="u2-compChip"
                            data-tip={c.tip}
                            style={
                              c.isRequired
                                ? {
                                    borderColor: 'hsl(var(--u2-accent-h) 86% 60% / 0.38)',
                                    background: 'hsl(var(--u2-accent-h) 86% 60% / 0.10)',
                                    color: 'var(--u2-text-1)'
                                  }
                                : undefined
                            }
                          >
                            {c.label}
                          </span>
                        ))
                      ) : (
                        <span className="u2-muted" style={{ fontSize: 12 }}>
                          Список компонентов будет доступен после загрузки структуры шаблона.
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
                  <div className="u2-cardInner">
                    <div className="u2-row" style={{ marginBottom: 10 }}>
                      <div>
                        <div className="u2-strong">Документы сайта</div>
                        <div className="u2-muted" style={{ fontSize: 12 }}>
                          Набор документов зависит от юрисдикции/платформы.
                        </div>
                      </div>
                      <span className="u2-badge">{legalDocsForReview.length}</span>
                    </div>

                    <details className="u2-documentDetails"><summary>Показать документы ({legalDocsForReview.length})</summary><div className="u2-compChips">
                      {legalDocsForReview.map((doc) => (
                        <div key={doc.id} className="u2-card" style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}>
                          <div className="u2-cardInner">
                            <div className="u2-strong" style={{ fontSize: 13 }}>
                              {doc.title}
                            </div>

                          </div>
                        </div>
                      ))}
                    </div></details>
                  </div>
                </div>

                {isAdmin && (
                  <div className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
                    <div className="u2-cardInner">
                      <div className="u2-row" style={{ marginBottom: 10 }}>
                        <div>
                          <div className="u2-strong">Пакетная генерация</div>
                          <div className="u2-muted" style={{ fontSize: 12 }}>
                            {isBatchTestMode
                              ? `Будет выполнен последовательный прогон ${selectedTestTemplateIds.length} выбранных тестовых шаблонов. Список компонентов берётся из каждого шаблона.`
                              : 'Запуск нескольких копий выбранного шаблона с отдельным статусом для каждой итерации.'}
                          </div>
                        </div>
                        {batchInProgress ? (
                          <span className="u2-pill is-warn">в процессе</span>
                        ) : (
                          <span className="u2-badge">{batchRuns.length || 0}</span>
                        )}
                      </div>

                      <div className="batch-controls">
                        <div className="u2-field">
                          <div className="u2-label">
                            {isBatchTestMode ? 'Тестовых шаблонов' : 'Количество копий'}
                          </div>
                          {isBatchTestMode ? (
                            <div className="u2-select" style={{ display: 'flex', alignItems: 'center' }}>
                              {selectedTestTemplateIds.length}
                            </div>
                          ) : (
                            <select
                              className="u2-select"
                              value={batchCount}
                              onChange={(e) => setBatchCount(Math.min(10, Math.max(1, Number(e.target.value))))}
                              disabled={batchInProgress}
                            >
                              {Array.from({ length: 10 }, (_, i) => i + 1).map((value) => (
                                <option key={value} value={value}>
                                  {value}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>

                        {!runAllTestTemplates && (
                          <div className="u2-field">
                            <div className="u2-label">Режим</div>
                            <div className="batch-mode-toggle">
                              <button
                                type="button"
                                className={`u2-btn ${batchMode === 'sequential' ? 'is-primary' : 'is-secondary'}`}
                                onClick={() => setBatchMode('sequential')}
                                disabled={batchInProgress}
                              >
                                Последовательно
                              </button>
                              <button
                                type="button"
                                className={`u2-btn ${batchMode === 'parallel' ? 'is-primary' : 'is-secondary'}`}
                                onClick={() => setBatchMode('parallel')}
                                disabled={batchInProgress}
                              >
                                Параллельно
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="u2-field">
                          <div className="u2-label">Запуск</div>
                          <button
                            type="button"
                            className="u2-btn is-success"
                            onClick={() => void startBatchGeneration()}
                            disabled={
                              batchInProgress ||
                              state.generationInProgress ||
                              (!isBatchTestMode && !formData.htmlTemplate) ||
                              (isBatchTestMode && selectedTestTemplateIds.length === 0)
                            }
                          >
                            {batchInProgress
                              ? '⏳ Запуск…'
                              : isBatchTestMode
                                ? `Запустить ${selectedTestTemplateIds.length} тестовых шаблонов`
                                : `Запустить ${batchCount} генераций`}
                          </button>
                        </div>
                      </div>

                      <div className="u2-toolbarGroup batch-actions">
                        <button
                          type="button"
                          className="u2-btn is-secondary"
                          onClick={() => void openAllBatchPreviews()}
                          disabled={batchPreviewUrls.length === 0}
                        >
                          Открыть все превью
                        </button>
                        <button
                          type="button"
                          className="u2-btn is-primary"
                          onClick={() => void downloadAllBatchSites()}
                          disabled={batchDownloadUrls.length === 0}
                        >
                          Скачать все сайты
                        </button>
                        <button type="button" className="u2-btn is-ghost" onClick={resetBatchRuns}>
                          Сбросить список
                        </button>
                      </div>

                      <div className="batch-hint">
                        Если браузер блокирует массовые действия, используйте ссылки ниже для ручного открытия/скачивания.
                      </div>

                      {batchRuns.length > 0 ? (
                        <div className="batch-table-wrapper">
                          <table className="batch-table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Шаблон</th>
                                <th>Статус</th>
                                <th>Время</th>
                                <th>Стадия</th>
                                <th>Очередь</th>
                                <th>previewUrl</th>
                                <th>downloadUrl</th>
                                <th>Ошибки</th>
                                <th>Действия</th>
                              </tr>
                            </thead>
                            <tbody>
                              {batchRuns.map((run) => {
                                const isExpanded = batchExpandedRuns.includes(run.index);
                                return (
                                  <React.Fragment key={run.index}>
                                    <tr
                                      className={`batch-row is-${run.status} ${isExpanded ? 'is-expanded' : ''}`}
                                      onClick={(event) => handleBatchRowClick(event, run.index)}
                                    >
                                      <td>#{run.index}</td>
                                      <td className="batch-template">
                                        {run.templateName || '—'}
                                        {run.templateId ? (
                                          <span className="u2-muted" style={{ fontSize: 11, marginLeft: 4 }}>
                                            #{run.templateId}
                                          </span>
                                        ) : null}
                                      </td>
                                      <td>
                                        <span className={`batch-status is-${run.status}`}>{run.status}</span>
                                      </td>
                                      <td>{formatBatchElapsed(run.elapsedMs)}</td>
                                      <td>
                                        <div className="batch-stage">
                                          {run.status === 'queued' && (
                                            <span>{run.stepName || 'В очереди'}</span>
                                          )}
                                          {run.status === 'processing' && (
                                            <>
                                              <span>{run.stepName || 'Генерация...'}</span>
                                              <span className="batch-stage-meta">{run.percentage}%</span>
                                            </>
                                          )}
                                          {run.status === 'completed' && (
                                            <span>Завершено</span>
                                          )}
                                          {run.status === 'failed' && (
                                            <span>Ошибка</span>
                                          )}
                                          {run.currentComponent && run.status === 'processing' && (
                                            <span className="batch-stage-meta">
                                              {run.currentComponent.name}
                                              {run.currentComponent.variantCode ? ` (${run.currentComponent.variantCode})` : ''}
                                            </span>
                                          )}
                                        </div>
                                      </td>
                                      <td>{run.status === 'queued' ? (run.queuePosition ?? '—') : '—'}</td>
                                      <td className="batch-url">
                                        {run.previewUrl ? (
                                          <a
                                            href={buildPreviewUrl(run.previewUrl)}
                                            onClick={(event) => {
                                              event.preventDefault();
                                              void openPreviewInNewTab(run.previewUrl || undefined);
                                            }}
                                          >
                                            {normalizePreviewUrl(run.previewUrl)}
                                          </a>
                                        ) : (
                                          '—'
                                        )}
                                      </td>
                                      <td className="batch-url">
                                        {run.downloadUrl ? (
                                          <a href={addTokenToUrl(run.downloadUrl)} download>
                                            {run.downloadUrl}
                                          </a>
                                        ) : (
                                          '—'
                                        )}
                                      </td>
                                      <td className="batch-error">{run.error || '—'}</td>
                                      <td>
                                        <div className="batch-row-actions">
                                          <button
                                            type="button"
                                            className="u2-btn is-ghost"
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              void openPreviewInNewTab(run.previewUrl || undefined);
                                            }}
                                            disabled={!run.previewUrl}
                                          >
                                            Открыть превью
                                          </button>
                                          <button
                                            type="button"
                                            className="u2-btn is-ghost"
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              void openPreviewInSameTab(run.previewUrl || undefined);
                                            }}
                                            disabled={!run.previewUrl}
                                          >
                                            В этой вкладке
                                          </button>
                                          {run.downloadUrl ? (
                                            <a
                                              href={addTokenToUrl(run.downloadUrl)}
                                              className="u2-btn is-ghost"
                                              download
                                              onClick={(event) => event.stopPropagation()}
                                            >
                                              Скачать
                                            </a>
                                          ) : (
                                            <button type="button" className="u2-btn is-ghost" disabled>
                                              Скачать
                                            </button>
                                          )}
                                          <button
                                            type="button"
                                            className="u2-btn is-ghost"
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              toggleBatchRunExpanded(run.index);
                                            }}
                                            aria-expanded={isExpanded}
                                          >
                                            {isExpanded ? 'Скрыть' : 'Подробнее'}
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                    {isExpanded && (
                                      <tr className="batch-row-details">
                                        <td colSpan={10}>{renderBatchProgressDetails(run)}</td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="u2-muted" style={{ fontSize: 12 }}>
                          Запусков пока нет. Выберите количество и режим, затем нажмите запуск.
                        </div>
                      )}

                      {batchPreviewBlockedUrls.length > 0 && (
                        <div className="batch-blocked">
                          <div className="u2-strong">Превью заблокированы браузером</div>
                          <div className="batch-link-list">
                            {batchPreviewBlockedUrls.map((url) => (
                              <a
                                key={url}
                                href={buildPreviewUrl(url)}
                                onClick={(event) => {
                                  event.preventDefault();
                                  void openPreviewInNewTab(url);
                                }}
                              >
                                {normalizePreviewUrl(url)}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {batchDownloadBlockedUrls.length > 0 && (
                        <div className="batch-blocked">
                          <div className="u2-strong">Скачивания заблокированы браузером</div>
                          <div className="batch-link-list">
                            {batchDownloadBlockedUrls.map((url) => (
                              <a key={url} href={addTokenToUrl(url)} download>
                                {url}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {isAdmin && (
                  <div className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
                    <div className="u2-cardInner">
                      <label className="u2-row" style={{ justifyContent: 'flex-start', gap: 10, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={formData.debugMode}
                          onChange={(e) => setFormData((prev) => ({ ...prev, debugMode: e.target.checked }))}
                        />
                        <span>
                          <span className="u2-strong">Режим отладки</span>
                          <br />
                          <span className="u2-muted" style={{ fontSize: 12 }}>
                            Показать техназвания и список компонентов.
                          </span>
                        </span>
                      </label>
                    </div>
                  </div>
                )}


              </div>
              </details>
            )}


          </div>
        )}
          </fieldset>

          {/* Навигация по шагам */}
          <StepNavigation
            currentStep={currentStep}
            onBack={handleStepBack}
            onNext={handleStepNext}
            nextStep={nextStep}
            nextDisabled={nextDisabled}
          busy={busy || batchInProgress}
            style={{ marginTop: 12 }}
          />
        </div>
      </section>

      {/* Модалка превью (шаблон/фон) */}
      {previewModal.isOpen && (
        <div className="u2-modal is-open" role="dialog" aria-modal="true" aria-label="Превью">
          <button className="u2-modalBackdrop" type="button" tabIndex={-1} onClick={closePreviewModal} aria-label="Закрыть" />
          <div className="u2-modalCard" style={{ maxWidth: 860 }}>
            <div className="u2-modalHeader">
              <div style={{ minWidth: 0 }}>
                <div className="u2-strong" title={previewModal.title || 'Превью'}>
                  {previewModal.title || 'Превью'}
                </div>
              </div>
              <button ref={previewCloseButtonRef} className="u2-iconBtn" type="button" onClick={closePreviewModal} aria-label="Закрыть">
                <svg className="u2-ico lg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="u2-modalBody">
              {previewModal.kind === 'template' && previewModal.template ? (
                <div>
                  <TemplateLayoutPreview template={previewModal.template} />
                  <p>Условная схема колонок. Расположение и вид блоков зависят от выбранных вариантов.</p>
                  <h3>Всегда в шаблоне</h3>
                  <div className="u2-compChips">{getTemplateComponents(previewModal.template).required.map(name => <span className="u2-compChip" key={name}>{getComponentLabel(name)}</span>)}</div>
                  <h3>Дополнительные блоки</h3>
                  <div className="u2-compChips">{getTemplateComponents(previewModal.template).optional.map(name => <span className="u2-compChip" key={name}>{getComponentLabel(name)}</span>)}</div>
                  <p className="u2-muted">В автоматическом режиме дополнительные блоки могут включаться случайно. Их можно настроить на шаге «Блоки».</p>
                  <button type="button" className="u2-btn is-primary" onClick={() => { handleFormChange('htmlTemplate', previewModal.template!.id); closePreviewModal(); }}>Выбрать этот шаблон</button>
                </div>
              ) : previewModal.kind === 'dynamic' ? (
                <div
                  style={{
                    height: 420,
                    borderRadius: 16,
                    overflow: 'hidden',
                    border: '1px solid var(--u2-border)',
                    position: 'relative',
                    background: 'var(--u2-surface-3)'
                  }}
                >
                  {previewModal.presetId !== dynamicPreview.presetId || dynamicPreview.loading ? (
                    <div className="u2-muted" style={{ fontSize: 12, padding: 12 }}>
                      Загрузка превью…
                    </div>
                  ) : null}
                  {!dynamicPreview.loading && dynamicPreview.error ? (
                    <div className="u2-muted" style={{ fontSize: 12, padding: 12 }}>
                      {dynamicPreview.error}
                    </div>
                  ) : null}
                  {!dynamicPreview.loading && !dynamicPreview.error && dynamicPreviewSrcDoc ? (
                    <iframe
                      title={previewModal.title || 'Превью'}
                      sandbox="allow-scripts"
                      tabIndex={-1}
                      srcDoc={dynamicPreviewSrcDoc}
                      style={{ width: '100%', height: '100%', border: 0 }}
                    />
                  ) : null}
                  {!dynamicPreview.loading && !dynamicPreview.error && !dynamicPreviewSrcDoc ? (
                    <div className="u2-muted" style={{ fontSize: 12, padding: 12 }}>
                      Нет данных превью.
                    </div>
                  ) : null}
                </div>
              ) : previewModal.imageUrl ? (
                <div
                  style={{
                    width: '100%',
                    maxWidth: 400,
                    aspectRatio: '1 / 2',
                    margin: '0 auto',
                    borderRadius: 16,
                    overflow: 'hidden',
                    border: '1px solid var(--u2-border)',
                    backgroundImage: backgroundPreviewImage(previewModal.imageUrl),
                    backgroundPosition: 'top center',
                    backgroundSize: 'cover'
                  }}
                />
              ) : (
                <div className="u2-muted" style={{ fontSize: 12 }}>
                  Нет данных превью.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Global tooltip (fixed, не режется overflow:hidden карточек) */}
      <div ref={tooltipRef} id="generator-tooltip" className="u2-tooltip" role="tooltip" aria-hidden="true" />
    </div>
  );
};

type GeneratorV2HeaderCardProps = {
  currentStep: number;
  totalSteps: number;
};

const GeneratorV2HeaderCard: React.FC<GeneratorV2HeaderCardProps> = ({ currentStep, totalSteps }) => {
  return (
    <section className="u2-card is-gradient">
      <div className="u2-cardInner">
        <div className="u2-cardHeader">
          <div>
            <h1 className="u2-title" style={{ fontSize: 22 }}>
              Генератор сайтов
            </h1>
            <p className="u2-subtitle">
              Выберите макет, опишите сайт и получите готовый архив.
            </p>
          </div>
          <span className="u2-badge is-accent">Шаг {currentStep} из {totalSteps}</span>
        </div>
      </div>
    </section>
  );
};

type GeneratorV2StepperItem = {
  step: GeneratorStep;
  label: string;
  hint: string;
};

type GeneratorV2StepperProps = {
  items: readonly GeneratorV2StepperItem[];
  currentStep: GeneratorStep;
  furthestStep: GeneratorStep;
  disabled: boolean;
  onSelectCompletedStep: (step: GeneratorStep) => void;
};

const GeneratorV2Stepper: React.FC<GeneratorV2StepperProps> = ({ items, currentStep, furthestStep, disabled, onSelectCompletedStep }) => {
  const cols = items.length;
  const stepperStyle = { '--u2-stepper-cols': String(cols) } as React.CSSProperties;

  return (
    <section className="u2-card is-gradient">
      <div className="u2-cardInner">
        <div
          className="u2-stepper"
          aria-label="Шаги генерации"
          style={stepperStyle}
        >
          {items.map((it, index) => {
            const isCurrent = it.step === currentStep;
            const isDone = it.step < currentStep;
            const isLocked = disabled || it.step > furthestStep;

            return (
              <div
                key={it.step}
                className={`u2-step ${isCurrent ? 'is-current' : ''} ${isDone ? 'is-done' : ''} ${isLocked ? 'is-locked' : ''}`}
                data-step={it.step}
                role="button"
                tabIndex={isLocked ? -1 : 0}
                aria-label={`${index + 1}. ${it.label}`}
                aria-current={isCurrent ? 'step' : undefined}
                aria-disabled={isLocked}
                onClick={() => {
                  if (!isLocked) {
                    onSelectCompletedStep(it.step);
                  }
                }}
                onKeyDown={(e) => {
                  if (isLocked) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectCompletedStep(it.step);
                  }
                }}
              >
                <div className="u2-stepNum">{index + 1}</div>
                <div className="u2-stepLabel">{it.label}</div>
                <div className="u2-stepHint">{it.hint}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

type StepNavigationProps = {
  currentStep: GeneratorStep;
  nextStep?: GeneratorStep;
  nextDisabled: boolean;
  busy?: boolean;
  onBack: () => void;
  onNext: () => void;
  style?: React.CSSProperties;
};

const StepNavigation: React.FC<StepNavigationProps> = ({
  currentStep,
  nextStep,
  nextDisabled,
  busy = false,
  onBack,
  onNext,
  style
}) => {
  return (
    <div className="u2-stepNav" style={style}>
      <button onClick={onBack} className="u2-btn is-ghost" disabled={currentStep === 1 || busy} type="button">
        Назад
      </button>

      <div className="u2-toolbarGroup">
        {currentStep < 7 && (
          <button onClick={onNext} className="u2-btn is-success" type="button" disabled={nextDisabled}>
            Далее: {nextStep ? GENERATOR_STEPS[nextStep]?.title : ''}
          </button>
        )}
      </div>
    </div>
  );
};

// ----------------------------
// Helper Functions (Pure / Safe)
// ----------------------------

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const readString = (value: unknown): string | undefined => {
  return typeof value === 'string' ? value : undefined;
};

const readNumber = (value: unknown): number | undefined => {
  return typeof value === 'number' ? value : undefined;
};

const readStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is string => typeof item === 'string');
};

const normalizeTemplateMetadata = (metadata: unknown): TemplateMetadata => {
  if (!isRecord(metadata)) {
    return {};
  }
  return metadata as TemplateMetadata;
};

const getTemplateComponentsRaw = (template: HtmlTemplate): Record<string, unknown> | null => {
  const templateRecord = template as unknown as Record<string, unknown>;
  return isRecord(templateRecord.components) ? templateRecord.components : null;
};

const getLegacyContainerType = (component: RecommendedComponent): string | undefined => {
  const componentRecord = component as unknown as Record<string, unknown>;
  return readString(componentRecord.containerType);
};

const getApiErrorStatus = (error: unknown): number | null => {
  if (!isRecord(error)) {
    return null;
  }
  const response = error.response;
  if (!isRecord(response)) {
    return null;
  }
  return typeof response.status === 'number' ? response.status : null;
};

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === 'string' && error.trim() !== '') {
    return error;
  }

  if (isRecord(error)) {
    const response = error.response;
    if (isRecord(response)) {
      const data = response.data;
      if (isRecord(data)) {
        const apiError = readString(data.error) || readString(data.message);
        if (apiError && apiError.trim() !== '') {
          return apiError;
        }
      }
    }

    const message = readString(error.message);
    if (message && message.trim() !== '') {
      return message;
    }
  }

  return fallback;
};

export function normalizeMultipageValue(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value === 1;
  }
  if (typeof value === 'string') {
    const normalized = value.toLowerCase().trim();
    return ['true', '1', 't', 'yes', 'multipage', 'multi-page'].includes(normalized);
  }
  return false;
}

export function isTemplateMultipage(template: HtmlTemplate): boolean {
  const metadata = template.metadata as Record<string, unknown> | undefined;

  // Резервируем несколько источников, чтобы шаблон не потерялся из-за формата флага
  return (
    normalizeMultipageValue(template.is_multipage) ||
    normalizeMultipageValue(metadata?.is_multipage) ||
    normalizeMultipageValue(metadata?.siteType)
  );
}

export function getTemplateComponents(template: HtmlTemplate | null): TemplateComponents {
  const raw = template ? getTemplateComponentsRaw(template) : null;
  // TypeScript: raw может быть null/undefined, поэтому используем безопасный доступ.
  // Логика не меняется: если массивов нет — возвращаем пустые списки.
  const requiredRaw = raw?.required;
  const optionalRaw = raw?.optional;
  const required = Array.isArray(requiredRaw) ? (requiredRaw as string[]) : [];
  const optional = Array.isArray(optionalRaw) ? (optionalRaw as string[]) : [];
  const totalFromLists = required.length + optional.length;
  const total = Number.isFinite(readNumber(raw?.total)) ? Number(raw?.total) : totalFromLists;
  return { required, optional, total };
}

export function getTemplateColumnsCount(template: HtmlTemplate): number {
  const meta = normalizeTemplateMetadata(template.metadata);
  const layoutColumns = isRecord(meta.layout) ? meta.layout.columns : undefined;
  const direct = meta.columns_count ?? meta.columnsCount ?? meta.columns ?? layoutColumns;
  if (Array.isArray(direct)) {
    const n = direct.length;
    if (n > 0) return n;
  }
  const num = Number(direct);
  if (Number.isFinite(num) && num > 0) return Math.trunc(num);

  const type = String(template.template_type || '').toLowerCase();
  const match = type.match(/(\d)\s*-\s*column|(\d)\s*column/);
  if (match) {
    const n = Number(match[1] || match[2]);
    if (Number.isFinite(n) && n > 0) return Math.trunc(n);
  }

  if (isTemplateMultipage(template)) return 2;
  return 1;
}

export function countColumnComponents(template: HtmlTemplate): number {
  const comps = getTemplateComponents(template);
  const list = [...comps.required, ...comps.optional];
  return list.filter((x) => String(x).toLowerCase().includes('column')).length;
}

export function getComponentTip(rawName: unknown): string {
  const raw = typeof rawName === 'string' ? rawName : String(rawName ?? '');
  const key = raw.toLowerCase();

  // Базовые подсказки (для UI-навода в генераторе)
  const map: Record<string, string> = {
    hero: 'Заголовок, описание предложения и основное действие.',
    header: 'Логотип и верхнее меню.',
    nav: 'Навигация: меню и ссылки.',
    menu: 'Навигация: меню и ссылки.',
    footer: 'Ссылки, контакты и документы внизу страницы.',
    faq: 'Ответы на частые вопросы.',
    gallery: 'Галерея: изображения/слайды.',
    testimonials: 'Отзывы о компании или продукте.',
    features: 'Особенности и преимущества предложения.',
    pricing: 'Цены/тарифы: планы и стоимость.',
    contact: 'Контактные данные и форма обратной связи.',
    about: 'Информация о компании и её истории.', services: 'Описание услуг или направлений работы.', cta: 'Предложение совершить действие: связаться, подписаться или заказать.', blog: 'Статьи и новости.', team: 'Сотрудники и их роли.', portfolio: 'Примеры выполненных работ.',
    legal: 'Юридический блок: документы/уведомления/согласия.'
  };

  if (map[key]) return map[key];
  if (key.includes('hero')) return map.hero;
  if (key.includes('header')) return map.header;
  if (key.includes('nav')) return map.nav;
  if (key.includes('menu')) return map.menu;
  if (key.includes('footer')) return map.footer;
  if (key.includes('faq')) return map.faq;
  if (key.includes('gallery')) return map.gallery;
  if (key.includes('testim')) return map.testimonials;
  if (key.includes('feature')) return map.features;
  if (key.includes('price')) return map.pricing;
  if (key.includes('contact') || key.includes('lead') || key.includes('form')) return map.contact;
  if (key.includes('legal') || key.includes('privacy') || key.includes('cookies')) return map.legal;

  return `Компонент: ${raw}`;
}

const LEGAL_DOC_NAMES: Record<string, string> = {
  'privacy-policy': 'Политика конфиденциальности',
  'terms-of-service': 'Условия использования',
  'cookie-policy': 'Политика Cookie',
  disclaimer: 'Отказ от ответственности',
  'gdpr-rights': 'Права GDPR',
  'ccpa-notice': 'Уведомление CCPA',
  'do-not-sell': 'Не продавать мои данные',
  'personal-data-consent': 'Согласие на обработку данных',
  'advertising-disclosure': 'Раскрытие рекламы',
  'community-guidelines': 'Правила сообщества'
};

export default GeneratorV2;

function getComponentLabel(name: string): string {
  const labels: Record<string, string> = { header: 'Шапка сайта', hero: 'Первый экран', navigation: 'Навигация', footer: 'Подвал сайта', about: 'О компании', services: 'Услуги', features: 'Преимущества', testimonials: 'Отзывы', pricing: 'Цены и тарифы', gallery: 'Галерея', portfolio: 'Портфолио', blog: 'Блог', faq: 'Вопросы и ответы', team: 'Команда', cta: 'Призыв к действию', contact: 'Контакты' };
  return labels[name] || name;
}

  const resolveRuleVariantChoice = (comp: RecommendedComponent): string | null => {
    if (comp.ruleSelectionMode !== 'specific') {
      return null;
    }
    const ruleName = (comp.ruleComponentName || '').trim();
    if (!ruleName) {
      return null;
    }
    const normalizedRule = ruleName.toLowerCase();
    const variants = comp.availableVariants || [];
    const exactFull = variants.find((v) => v.fullName.toLowerCase() === normalizedRule);
    if (exactFull) {
      return exactFull.variantCode;
    }
    const exactCode = variants.find((v) => v.variantCode.toLowerCase() === normalizedRule);
    if (exactCode) {
      return exactCode.variantCode;
    }
    const baseName = (comp.componentName || '').toLowerCase();
    if (baseName && normalizedRule === baseName && variants.length === 1) {
      return variants[0].variantCode;
    }
    if (baseName && normalizedRule.startsWith(`${baseName}-`)) {
      const derivedCode = normalizedRule.slice(baseName.length + 1);
      const derivedMatch = variants.find((v) => v.variantCode.toLowerCase() === derivedCode);
      if (derivedMatch) {
        return derivedMatch.variantCode;
      }
    }
    return null;
  };


function TemplateLayoutPreview({ template }: { template: HtmlTemplate }) {
  const columns = Math.min(3, Math.max(1, getTemplateColumnsCount(template)));
  const components = getTemplateComponents(template);
  const items = [...components.required.map(name => ({ name, optional: false })), ...components.optional.map(name => ({ name, optional: true }))];
  const top = items.filter(item => /header|navigation|menu/.test(item.name));
  const bottom = items.filter(item => /footer/.test(item.name));
  const middle = items.filter(item => !top.includes(item) && !bottom.includes(item));
  const block = (item: {name: string; optional: boolean}, index: number) => <div key={`${item.name}-${index}`} className={`wp-diagramBlock ${item.optional ? 'is-optional' : ''} ${/hero|gallery|portfolio/.test(item.name) ? 'is-visual' : ''}`}><span>{getComponentLabel(item.name)}</span><div aria-hidden="true"><i/><i/><i/></div></div>;
  return <div className="u2-templateDiagram wp-templateDiagram" aria-label={`Схема: ${columns} кол., ${components.total} типов блоков`}>
    <div className="wp-diagramTop">{top.length ? top.map(block) : <span>Верхняя область</span>}</div>
    <div className="u2-diagramColumns" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
      {Array.from({length: columns}, (_, index) => <div className="wp-diagramColumn" key={index}>{middle.filter((_, i) => i % columns === index).slice(0, 5).map(block)}</div>)}
    </div>
    {middle.length > columns * 5 && <small>Ещё {middle.length - columns * 5} типов блоков</small>}
    <div className="wp-diagramBottom">{bottom.length ? bottom.map(block) : <span>Нижняя область</span>}</div>
    <small>{components.required.length} обязательных · {components.optional.length} дополнительных типов</small>
  </div>;
}
